import React, { useEffect, useMemo, useState } from 'react';
import { announceTicketTitle } from '../utils/ticketTitleEvents';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { loadDiagnosticReport, normalizeDiagnosticReportForUi, saveDiagnosticReport, getSafeToDriveLevel, SAFE_TO_DRIVE_STYLES } from '../utils/diagnosticReport';
import { getApiBaseUrl, getTesterModeHeaders, getUserDetailsBaseUrl, setStoredThreadId, getPhoneDisplayParts } from '../utils/chatIdentity';
import {
    formatPartnerMoney,
    formatPartnerScheduledAppointment,
    getPartnerStatusLabel,
    listPartnerShops,
    listPartnerRepairRequests,
} from '../utils/partnerShopApi';

const PENDING_CHAT_INTENT_STORAGE_KEY = 'dash_pending_chat_intent';
const CHAT_API_BASE_URL = `${getApiBaseUrl()}/api/chat`;

const SectionTitle = ({ children }) => (
    <h2 className="mb-2.5 mt-5 text-[11px] font-black uppercase tracking-[0.1em] text-[#414141] sm:mb-3 sm:mt-7 sm:text-[12px] sm:tracking-[0.12em]">
        {children}
    </h2>
);

const EmptyReport = ({ onBack }) => (
    <div className="flex h-full w-full items-center justify-center bg-[#F9F9F9] p-6 text-center">
        <div className="max-w-sm rounded-2xl border border-[#d9d9d9] bg-white p-6 shadow-sm">
            <h1 className="text-xl font-black text-black">Report not available</h1>
            <p className="mt-2 text-sm font-semibold text-black/50">
                Open the symptom report card from the chat again.
            </p>
            <button
                type="button"
                onClick={onBack}
                className="mt-5 w-full rounded-full bg-black px-5 py-3 text-sm font-black text-white transition-all hover:bg-black/85"
            >
                Back to chat
            </button>
        </div>
    </div>
);

const ChevronButton = ({ direction, onClick, disabled = false }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all ${
            disabled
                ? 'cursor-not-allowed border-[#d9d9d9] bg-[#F9F9F9] text-[#afafaf]'
                : 'border-[#d9d9d9] bg-white text-[#414141] hover:bg-[#f5f5f5]'
        }`}
        aria-label={direction === 'next' ? 'Next likely cause' : 'Previous likely cause'}
    >
        <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            {direction === 'next' ? <path d="m9 18 6-6-6-6" /> : <path d="m15 18-6-6 6-6" />}
        </svg>
    </button>
);

const InfoBadge = ({ children, className = '' }) => (
    <span className={`rounded-full px-3 py-1 text-[11px] font-black ${className}`}>
        {children}
    </span>
);

const DifficultyBadge = ({ value }) => {
    const normalized = String(value || 'Medium').toLowerCase();
    const style = normalized === 'easy'
        ? 'bg-[#dff7e7] text-[#159947]'
        : normalized === 'hard'
        ? 'bg-[#ffe6e6] text-[#d44848]'
        : 'bg-[#fff1d9] text-[#b7791f]';

    return <span className={`inline-flex rounded-full px-3 py-1 text-sm font-black ${style}`}>{value}</span>;
};

const MetricBlock = ({ label, value, valueClassName = 'text-[#414141]', children }) => (
    <div>
        <p className="text-[11px] font-medium text-[#414141]">{label}</p>
        {value ? <p className={`mt-1 text-[15px] font-black ${valueClassName}`}>{value}</p> : children}
    </div>
);

const getGarageRequestTitle = (request) => (
    request?.services?.[0]?.title || request?.shop_ready_summary?.customerComplaint || 'Garage repair request'
);

const REFERRAL_LOCK_RELEASE_STATUSES = new Set(['declined', 'cancelled', 'failed']);

const getGarageStatusClass = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (['ready_for_pickup', 'picked_up', 'scheduled', 'estimate_accepted'].includes(normalized)) {
        return 'border-[#bde7d5] bg-[#ecfff5] text-[#087a43]';
    }
    if (['estimate_sent', 'in_service'].includes(normalized)) {
        return 'border-[#dfd0ff] bg-[#f7f2ff] text-[#6f3cc3]';
    }
    if (['declined', 'failed'].includes(normalized)) {
        return 'border-[#ffd2d2] bg-[#fff1f1] text-[#b42318]';
    }
    return 'border-[#d9d9d9] bg-white text-[#414141]';
};

const getGarageDeclineReason = (request) => {
    const notes = String(request?.notes || '').trim();
    if (!notes || /^Customer requested an estimate/i.test(notes)) return '';
    return notes;
};

const GarageProgressSummary = ({ requests = [], isLoading = false, error = '', onOpenChat, onOpenGarage }) => {
    if (isLoading && !requests.length) {
        return (
            <div className="mt-5 rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4 text-sm font-bold text-[#414141]">
                Loading Garage progress...
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-5 rounded-2xl border border-[#ffd2d2] bg-[#fff1f1] px-4 py-3 text-sm font-bold text-[#b42318]">
                {error}
            </div>
        );
    }

    if (!requests.length) {
        return (
            <div className="mt-5 rounded-2xl border border-dashed border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4">
                <p className="text-sm font-black text-[#414141]">No Garage chat started yet</p>
                <p className="mt-1 text-sm font-semibold leading-relaxed text-[#414141]">
                    Start an estimate request from Garage. This report will stay read-only while shop messages and approvals happen there.
                </p>
                <button
                    type="button"
                    onClick={onOpenGarage}
                    className="mt-3 inline-flex min-h-[38px] items-center justify-center rounded-xl border border-[#d9d9d9] bg-white px-4 text-sm font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26]"
                >
                    Open Garage
                </button>
            </div>
        );
    }

    return (
        <div className="mt-5 space-y-3">
            {requests.map((request) => {
                const estimate = request.latest_estimate;
                const appointment = formatPartnerScheduledAppointment(request);
                const declineReason = getGarageDeclineReason(request);
                const recentActivity = (request.timeline || []).slice(-3).reverse();
                return (
                    <div key={request.request_id} className="rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.08em] ${getGarageStatusClass(request.request_status)}`}>
                                        {getPartnerStatusLabel(request.request_status)}
                                    </span>
                                    <span className="text-xs font-bold text-[#414141]">
                                        {request.partner_shop?.name || 'Partner shop'}
                                    </span>
                                </div>
                                <p className="mt-2 text-sm font-black text-[#414141]">{getGarageRequestTitle(request)}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => onOpenChat(request)}
                                className="inline-flex min-h-[38px] shrink-0 items-center justify-center rounded-xl bg-[#414141] px-4 text-sm font-black text-white transition hover:bg-[#2A2A2A]"
                            >
                                Message shop
                            </button>
                        </div>

                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                            <MetricBlock
                                label="Latest Estimate"
                                value={estimate ? formatPartnerMoney(estimate.total_amount, estimate.currency) : 'Waiting for estimate'}
                                valueClassName={estimate ? 'text-[#414141]' : 'text-[#414141]'}
                            />
                            <MetricBlock
                                label="Appointment"
                                value={appointment || 'Not scheduled'}
                                valueClassName={appointment ? 'text-[#087a43]' : 'text-[#414141]'}
                            />
                        </div>

                        {request.request_status === 'declined' && (
                            <div className="mt-3 rounded-xl border border-[#ffd2d2] bg-white px-3 py-2">
                                <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#b42318]">Decline note</p>
                                <p className="mt-1 text-sm font-semibold text-[#414141]">
                                    {declineReason || 'No reason provided.'}
                                </p>
                            </div>
                        )}

                        {recentActivity.length > 0 && (
                            <div className="mt-4 border-t border-[#d9d9d9] pt-3">
                                <p className="text-[10px] font-black uppercase tracking-[0.08em] text-[#414141]">Recent activity</p>
                                <div className="mt-2 space-y-1.5">
                                    {recentActivity.map((item, index) => (
                                        <p key={`${item.event || 'event'}-${item.created_at || index}`} className="text-sm font-semibold text-[#414141]">
                                            {item.label || item.event || 'Update'}
                                            {item.created_at ? (
                                                <span className="font-medium text-[#9e9e9e]"> · {new Date(item.created_at).toLocaleString()}</span>
                                            ) : null}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// Recommended service on the shop card should name the actual likely repair — not default to a
// generic "General System Diagnostic" that doesn't match the diagnosis.
const getRecommendedServiceLabel = (cause) => {
    const repair = String(cause?.likelyRepair || '').trim();
    if (repair && !/^diy repair plan$/i.test(repair)) return repair;
    const source = `${cause?.title || ''} ${cause?.recommendedCheck || cause?.recommendedCheckDetail?.text || ''}`.toLowerCase();
    if (source.includes('battery') || source.includes('charging')) return 'Battery / Charging System Diagnostic';
    if (source.includes('starter')) return 'Starter / Starting System Diagnostic';
    if (source.includes('brake')) return 'Brake System Diagnostic';
    return 'General System Diagnostic';
};


// Display a phone as "+1 908-507-0021" (#421): country code from getPhoneDisplayParts,
// national number grouped 3-3-4 for US-style 10-digit numbers. No placeholder fallback —
// an empty phone returns '' so callers can omit it.
const formatPhone = (value = '') => {
    const { countryCode, nationalNumber } = getPhoneDisplayParts(value);
    if (!nationalNumber) return '';
    // US/Canada (+1) 10-digit → 3-3-4 grouping ("+1 908-507-0021"); other countries keep the
    // national block intact so non-US numbers aren't mis-grouped.
    const national = (countryCode === '+1' && nationalNumber.length === 10)
        ? `${nationalNumber.slice(0, 3)}-${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`
        : nationalNumber;
    return countryCode ? `${countryCode} ${national}` : national;
};

// A customer email of "@carfido.com" is a fabricated placeholder from the old
// name-slug generator — never a real driver address — so it must never be shown to a shop.
const isPlaceholderCustomerEmail = (email = '') => /@carfido\.com\s*$/i.test(String(email || '').trim());

const normalizeIssueKey = (value = '') => String(value || '')
    .toLowerCase()
    .replace(/won['’]?t/g, 'will not')
    .replace(/\bcan['’]?t\b/g, 'cannot')
    .replace(/\bdoesn['’]?t\b/g, 'does not')
    .replace(/\bisn['’]?t\b/g, 'is not')
    .replace(/\bno[\s-]*start(?:ing)?\b/g, 'no start')
    .replace(/\bnot starting\b/g, 'no start')
    .replace(/\bwill not start\b/g, 'no start')
    .replace(/\bwon?t start\b/g, 'no start')
    .replace(/\bopening\b/g, 'open')
    .replace(/\bopens\b/g, 'open')
    .replace(/\bopened\b/g, 'open')
    .replace(/\brotating\b/g, 'rotate')
    .replace(/\brotates\b/g, 'rotate')
    .replace(/\bcar\b/g, ' ')
    .replace(/\bvehicle\b/g, ' ')
    .replace(/\bmy\b/g, ' ')
    .replace(/\bis\b/g, ' ')
    .replace(/\bthe\b/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\bstarting\b/g, 'start')
    .replace(/\bstarts\b/g, 'start')
    .replace(/\s+/g, ' ')
    .trim();

const getTicketVehicleLabel = (ticket) => (
    ticket?.vehicle_label ||
    ticket?.vehicleLabel ||
    [ticket?.vehicle?.year, ticket?.vehicle?.make, ticket?.vehicle?.model].filter(Boolean).join(' ').trim()
);

const getReportTicketId = (report) => (
    report?.diagnosticTicketId ||
    report?.diagnostic_ticket_id ||
    report?.ticketId ||
    report?.ticket_id ||
    ''
);

const getRealTicketId = (ticket) => (
    ticket?.diagnostic_ticket_id ||
    ticket?.diagnosticTicketId ||
    ticket?.ticket_id ||
    ticket?.ticketId ||
    ticket?.id ||
    ''
);

const getTicketDisplayNumber = (ticket) => (
    ticket?.display_ticket_number ||
    ticket?.displayTicketNumber ||
    ticket?.ticket_number ||
    ticket?.ticketNumber ||
    ''
);

const getTicketThreadId = (ticket) => (
    ticket?.thread_id ||
    ticket?.threadId ||
    ticket?.source_repair_log_id ||
    ticket?.sourceRepairLogId ||
    ticket?.repair_log_id ||
    ticket?.repairLogId ||
    ''
);

const getTicketTitle = (ticket) => (
    ticket?.title ||
    ticket?.symptom_summary ||
    ticket?.symptomSummary ||
    ticket?.issue ||
    ticket?.issueTitle ||
    ''
);

const getTicketPhone = (ticket) => (
    ticket?.phone_number ||
    ticket?.phoneNumber ||
    ticket?.customer_phone ||
    ticket?.customerPhone ||
    ''
);

const getReportPhone = (report) => (
    report?.customerPhone ||
    report?.customer_phone ||
    report?.phone_number ||
    report?.phoneNumber ||
    ''
);

const isReportClosedStatus = (report) => {
    const status = String(report?.ticketStatus || report?.ticket_status || report?.status || '').toLowerCase();
    return status === 'closed' || status === 'abandoned';
};

const mergeInitialReportWithStoredCopy = (routeReport, storedReport) => {
    const normalizedRouteReport = normalizeDiagnosticReportForUi(routeReport);
    const normalizedStoredReport = normalizeDiagnosticReportForUi(storedReport);

    if (!normalizedRouteReport) return normalizedStoredReport;
    if (!normalizedStoredReport) return normalizedRouteReport;

    const storedClosed = isReportClosedStatus(normalizedStoredReport);
    const routeClosed = isReportClosedStatus(normalizedRouteReport);
    const mergedReport = storedClosed
        ? { ...normalizedRouteReport, ...normalizedStoredReport }
        : { ...normalizedStoredReport, ...normalizedRouteReport };

    // DIY progress (completed guided checks / repair guides) may exist on only one
    // of the two copies — e.g. the stored copy was wiped by an older refresh while
    // the route copy still has them. Keep whichever source actually has entries so
    // the checks survive into a closed report regardless of merge precedence.
    const pickNonEmpty = (field) => {
        const stored = normalizedStoredReport[field];
        const route = normalizedRouteReport[field];
        if (Array.isArray(stored) && stored.length) return stored;
        if (Array.isArray(route) && route.length) return route;
        return mergedReport[field] || [];
    };
    mergedReport.completedGuidedChecks = pickNonEmpty('completedGuidedChecks');
    mergedReport.completedRepairGuides = pickNonEmpty('completedRepairGuides');

    if (storedClosed || routeClosed) {
        mergedReport.ticketStatus = 'closed';
        mergedReport.ticketStage = 'closed';
        mergedReport.status = 'Closed';
    }

    return normalizeDiagnosticReportForUi(mergedReport);
};

const getTicketStatus = (ticket) => String(ticket?.status || ticket?.ticketStatus || '').toLowerCase();

const isTicketClosed = (ticket) => {
    const status = getTicketStatus(ticket);
    return status === 'closed' || status === 'abandoned';
};

const ticketVehicleMatchesReport = (ticket, reportVehicleKey) => {
    const ticketVehicleKey = String(getTicketVehicleLabel(ticket) || '').trim().toLowerCase();
    return !reportVehicleKey || !ticketVehicleKey || ticketVehicleKey === reportVehicleKey;
};

const issueKeysMatch = (ticketIssueKey = '', reportIssueKey = '') => {
    if (!ticketIssueKey || !reportIssueKey) return false;
    if (ticketIssueKey === reportIssueKey) return true;
    if (ticketIssueKey.includes(reportIssueKey) || reportIssueKey.includes(ticketIssueKey)) return true;

    const ticketTokens = ticketIssueKey.split(' ').filter(Boolean);
    const reportTokens = reportIssueKey.split(' ').filter(Boolean);
    const reportTokenSet = new Set(reportTokens);
    const sharedCount = ticketTokens.filter((token) => reportTokenSet.has(token)).length;
    const requiredShared = Math.min(2, ticketTokens.length, reportTokens.length);

    return sharedCount >= requiredShared;
};

const findDiagnosticTicketForReport = (report, tickets = []) => {
    if (!report || !Array.isArray(tickets) || tickets.length === 0) return null;

    const reportClosed = isReportClosedStatus(report);
    const reportTicketId = getReportTicketId(report);
    if (reportTicketId) {
        const idMatch = tickets.find((ticket) => String(getRealTicketId(ticket)) === String(reportTicketId));
        if (idMatch) return idMatch;
    }

    const reportThreadId = report.threadId || report.thread_id || '';
    if (reportThreadId) {
        const threadMatch = tickets.find((ticket) => String(getTicketThreadId(ticket)) === String(reportThreadId));
        if (threadMatch) return threadMatch;
    }

    const reportIssueKey = normalizeIssueKey(report.issue || report.title || report.symptom_summary || '');
    const reportVehicleKey = String(report.vehicle || report.vehicleLabel || report.vehicle_label || '').trim().toLowerCase();

    const issueMatches = reportIssueKey
        ? tickets.filter((ticket) => {
            const ticketIssueKey = normalizeIssueKey(getTicketTitle(ticket));
            return ticketVehicleMatchesReport(ticket, reportVehicleKey) && issueKeysMatch(ticketIssueKey, reportIssueKey);
        })
        : [];
    if (issueMatches.length) {
        return reportClosed
            ? (issueMatches.find(isTicketClosed) || issueMatches[0])
            : (issueMatches.find((ticket) => !isTicketClosed(ticket)) || issueMatches[0]);
    }

    const reportTicketNumber = report.ticketNumber || report.ticket_number || '';
    if (reportTicketNumber) {
        const numberMatches = tickets.filter((ticket) => (
            String(getTicketDisplayNumber(ticket)) === String(reportTicketNumber) &&
            ticketVehicleMatchesReport(ticket, reportVehicleKey)
        ));
        if (numberMatches.length === 1) return numberMatches[0];
        if (numberMatches.length > 1) {
            return reportClosed
                ? (numberMatches.find(isTicketClosed) || numberMatches[0])
                : (numberMatches.find((ticket) => !isTicketClosed(ticket)) || numberMatches[0]);
        }
    }

    const vehicleMatches = tickets.filter((ticket) => ticketVehicleMatchesReport(ticket, reportVehicleKey));
    const openVehicleMatches = vehicleMatches.filter((ticket) => !isTicketClosed(ticket));
    if (openVehicleMatches.length === 1) return openVehicleMatches[0];

    return null;
};

const addUniqueTicketCandidate = (candidates, ticket) => {
    if (!ticket) return;
    const key = [
        getRealTicketId(ticket),
        getTicketThreadId(ticket),
        getTicketDisplayNumber(ticket),
        getTicketTitle(ticket),
        getTicketVehicleLabel(ticket),
    ].filter(Boolean).join('|');
    if (!key) return;
    if (candidates.some((candidate) => {
        const candidateKey = [
            getRealTicketId(candidate),
            getTicketThreadId(candidate),
            getTicketDisplayNumber(candidate),
            getTicketTitle(candidate),
            getTicketVehicleLabel(candidate),
        ].filter(Boolean).join('|');
        return candidateKey === key;
    })) return;
    candidates.push(ticket);
};

const fetchActiveDiagnosticTicketForClose = async (phone) => {
    if (!phone) return null;
    try {
        const response = await fetch(
            `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/active`
        );
        if (!response.ok) return null;
        const data = await response.json();
        return data?.ticket || null;
    } catch {
        return null;
    }
};

const resolveCloseableTicketForReport = async ({
    report,
    matchingTicket,
    activeDiagnosticTicket,
    recentDiagnostics,
    refreshDiagnosticTickets,
    phone,
}) => {
    const candidates = [];
    addUniqueTicketCandidate(candidates, matchingTicket);
    addUniqueTicketCandidate(candidates, activeDiagnosticTicket);
    (recentDiagnostics || []).forEach((ticket) => addUniqueTicketCandidate(candidates, ticket));

    let resolvedTicket = findDiagnosticTicketForReport(report, candidates);
    if (resolvedTicket && getRealTicketId(resolvedTicket)) return resolvedTicket;

    if (refreshDiagnosticTickets) {
        const refreshedTickets = await refreshDiagnosticTickets();
        (refreshedTickets || []).forEach((ticket) => addUniqueTicketCandidate(candidates, ticket));
        resolvedTicket = findDiagnosticTicketForReport(report, candidates);
        if (resolvedTicket && getRealTicketId(resolvedTicket)) return resolvedTicket;
    }

    const activeTicket = await fetchActiveDiagnosticTicketForClose(phone);
    addUniqueTicketCandidate(candidates, activeTicket);
    resolvedTicket = findDiagnosticTicketForReport(report, candidates);
    if (resolvedTicket && getRealTicketId(resolvedTicket)) return resolvedTicket;

    const reportVehicleKey = String(report?.vehicle || report?.vehicleLabel || report?.vehicle_label || '').trim().toLowerCase();
    const openVehicleMatches = candidates.filter((ticket) => (
        getRealTicketId(ticket) &&
        !isTicketClosed(ticket) &&
        ticketVehicleMatchesReport(ticket, reportVehicleKey)
    ));
    if (openVehicleMatches.length === 1) return openVehicleMatches[0];

    const openMatches = candidates.filter((ticket) => getRealTicketId(ticket) && !isTicketClosed(ticket));
    if (openMatches.length === 1) return openMatches[0];

    return resolvedTicket || null;
};

const parseOptionalNumericTicketId = (value) => {
    if (value == null || value === '') return null;
    const text = String(value).trim();
    if (!/^\d+$/.test(text)) return null;
    const parsed = Number(text);
    return Number.isSafeInteger(parsed) ? parsed : null;
};

const closeMatchingTicketForReport = async ({
    phone,
    report,
    resolvedTicket,
    summary,
}) => {
    if (!phone) {
        throw new Error('Ticket details are missing from this report.');
    }

    const ticketThreadId = getTicketThreadId(resolvedTicket);
    const response = await fetch(
        `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(phone)}/close-matching`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                diagnostic_ticket_id: parseOptionalNumericTicketId(getRealTicketId(resolvedTicket) || getReportTicketId(report)),
                ticket_number: String(getTicketDisplayNumber(resolvedTicket) || report?.ticketNumber || report?.ticket_number || ''),
                thread_id: ticketThreadId || report?.threadId || report?.thread_id || '',
                source_repair_log_id: parseOptionalNumericTicketId(ticketThreadId || report?.threadId || report?.thread_id),
                vehicle_id: parseOptionalNumericTicketId(resolvedTicket?.vehicle_id || report?.vehicle_id || report?.vehicleId),
                vehicle_label: getTicketVehicleLabel(resolvedTicket) || report?.vehicle || report?.vehicleLabel || report?.vehicle_label || '',
                issue: report?.issue || report?.title || report?.symptom_summary || '',
                title: getTicketTitle(resolvedTicket) || report?.issue || report?.title || '',
                symptom_summary: resolvedTicket?.symptom_summary || resolvedTicket?.symptomSummary || report?.issue || report?.symptom_summary || '',
                outcome: 'successful',
                summary,
                diy_checks: Array.isArray(report?.completedGuidedChecks) ? report.completedGuidedChecks : [],
            }),
        }
    );

    if (!response.ok) {
        let errorMessage = 'Failed to close ticket';
        try {
            const errorData = await response.json();
            errorMessage = errorData?.detail || errorData?.error || errorMessage;
        } catch {}
        throw new Error(errorMessage);
    }

    const closedTicket = await response.json();
    if (closedTicket?.error) {
        throw new Error(closedTicket.error || 'Failed to close ticket');
    }
    return closedTicket;
};

const ToolChecklistItem = ({ tool, checked, onToggle, isOwned }) => (
    <label className="flex cursor-pointer items-start gap-3 px-4 py-4">
        <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            className="mt-1 h-5 w-5 rounded border-[#c8c8c8] text-[#414141] focus:ring-[#D81F26]"
        />
        <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-[15px] font-semibold text-[#414141]">{tool.name}</p>
                {tool.optional && (
                    <span className="inline-flex items-center rounded-full bg-[#f2f2f2] px-2 py-0.5 text-[11px] font-black uppercase tracking-[0.04em] text-[#737373]">
                        Optional
                    </span>
                )}
                {isOwned && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#dff7e7] px-2 py-0.5 text-[11px] font-black text-[#0a8f53]">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                        You own this
                    </span>
                )}
                {isOwned === false && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#fff5e8] px-2 py-0.5 text-[11px] font-black text-[#b7791f]">
                        Not in inventory
                    </span>
                )}
            </div>
            {(tool.priceLabel || tool.link) && (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm font-medium text-[#414141]">
                    {tool.priceLabel && <span>{tool.priceLabel}</span>}
                    {tool.link && (
                        <a
                            href={tool.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D81F26] hover:underline"
                        >
                            View options
                        </a>
                    )}
                </div>
            )}
        </div>
    </label>
);

const ConfirmedDIYPlanSection = ({ cause, completedCheck }) => {
    if (!cause || !completedCheck) return null;

    const repairSteps = cause.repairSteps || cause.steps || [];
    const safetyPrecautions = cause.safetyPrecautions || [];
    const requiredTools = cause.requiredTools || [];
    const requiredParts = cause.requiredParts || [];

    return (
        <>
            <SectionTitle>Confirmed DIY Plan</SectionTitle>
            <section className="overflow-hidden rounded-[28px] border border-[#d9d9d9] bg-white shadow-sm">
                <div className="px-5 py-5">
                    <div className="rounded-[22px] border border-[#bfe9d0] bg-[#f2fbf5] px-4 py-4">
                        <p className="text-[15px] font-black text-[#0a8f53]">{completedCheck.outcomeLabel}</p>
                        <p className="mt-2 text-[16px] font-medium leading-relaxed text-[#414141]">{completedCheck.summary}</p>
                        {completedCheck.notes && (
                            <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#414141]">Notes: {completedCheck.notes}</p>
                        )}
                    </div>
                    <div className="mt-5 grid gap-4 md:grid-cols-3">
                        <div className="rounded-[22px] bg-[#F9F9F9] px-4 py-4">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Likely Repair</p>
                            <p className="mt-3 text-[16px] font-black leading-snug text-[#414141]">{cause.likelyRepair || 'DIY repair plan'}</p>
                        </div>
                        <div className="rounded-[22px] bg-[#F9F9F9] px-4 py-4">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Difficulty Rating</p>
                            <div className="mt-3">
                                <DifficultyBadge value={cause.diyDifficulty || 'Medium'} />
                            </div>
                        </div>
                        <div className="rounded-[22px] bg-[#F9F9F9] px-4 py-4">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Estimated Time</p>
                            <p className="mt-3 text-[16px] font-black leading-snug text-[#414141]">{cause.repairTime || cause.timeLabel || '30-90 min'}</p>
                        </div>
                    </div>

                    <div className="mt-5 rounded-[22px] border border-[#d9d9d9] bg-white">
                        <div className="border-b border-[#d9d9d9] px-4 py-3">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Required Tools / Materials</p>
                        </div>
                        <div className="grid gap-5 px-4 py-4 md:grid-cols-2">
                            <div>
                                <p className="text-[12px] font-black uppercase tracking-[0.08em] text-[#414141]">Tools</p>
                                <ul className="mt-3 space-y-2 text-[15px] font-medium text-[#414141]">
                                    {requiredTools.length > 0 ? requiredTools.map((tool) => (
                                        <li key={tool.name} className="flex items-start gap-2">
                                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#414141]" />
                                            <span>
                                                {tool.name}
                                                {tool.priceLabel ? <span className="text-[#414141]"> - {tool.priceLabel}</span> : null}
                                            </span>
                                        </li>
                                    )) : (
                                        <li className="text-[15px] font-medium text-[#414141]">Basic hand tools</li>
                                    )}
                                </ul>
                            </div>
                            <div>
                                <p className="text-[12px] font-black uppercase tracking-[0.08em] text-[#414141]">Materials / Parts</p>
                                <ul className="mt-3 space-y-2 text-[15px] font-medium text-[#414141]">
                                    {requiredParts.length > 0 ? requiredParts.map((part) => (
                                        <li key={part.name} className="flex items-start gap-2">
                                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#414141]" />
                                            <span>
                                                {part.name}
                                                {part.notes ? <span className="text-[#414141]"> - {part.notes}</span> : null}
                                            </span>
                                        </li>
                                    )) : (
                                        <li className="text-[15px] font-medium text-[#414141]">No replacement parts listed yet</li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {safetyPrecautions.length > 0 && (
                        <div className="mt-5 rounded-[22px] border border-[#d9d9d9] bg-white">
                            <div className="border-b border-[#d9d9d9] px-4 py-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Safety Precautions</p>
                            </div>
                            <ul className="space-y-3 px-4 py-4">
                                {safetyPrecautions.map((item, index) => (
                                    <li key={`${cause.id}-safety-${index}`} className="flex items-start gap-3">
                                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#fff5e8] text-[12px] font-black text-[#b7791f]">
                                            !
                                        </span>
                                        <span className="text-[15px] font-medium leading-relaxed text-[#414141]">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {repairSteps.length > 0 && (
                        <div className="mt-5 rounded-[22px] border border-[#d9d9d9] bg-white">
                            <div className="border-b border-[#d9d9d9] px-4 py-3">
                                <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">DIY Repair Steps</p>
                            </div>
                            <ol className="space-y-0 px-4 py-2">
                                {repairSteps.map((step, index) => {
                                    // Steps may be plain strings or {instruction, detail} objects.
                                    const instruction = (step && typeof step === 'object')
                                        ? (step.instruction || step.title || step.text || step.description || '')
                                        : step;
                                    const detail = (step && typeof step === 'object')
                                        ? (step.detail || (step.description && step.description !== instruction ? step.description : ''))
                                        : '';
                                    return (
                                        <li key={`${cause.id}-confirmed-step-${index}`} className={`flex items-start gap-4 py-4 ${index === repairSteps.length - 1 ? '' : 'border-b border-[#f2f2f2]'}`}>
                                            <div className="flex shrink-0 flex-col items-center">
                                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f1f1f1] text-[13px] font-black text-[#414141]">
                                                    {index + 1}
                                                </span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[13px] font-black uppercase tracking-[0.06em] text-[#6B6B6B]">Step {index + 1}</p>
                                                <p className="mt-1 text-[15px] font-medium leading-relaxed text-[#414141]">{instruction}</p>
                                                {detail && (
                                                    <p className="mt-1 text-[13px] font-medium leading-relaxed text-[#414141]">{detail}</p>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

const DiagnosticReportPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const outletContext = useOutletContext() || {};
    const {
        authState,
        displayName,
        recentDiagnostics = [],
        activeDiagnosticTicket,
        refreshDiagnosticTickets,
        onSelectThread,
    } = outletContext;
    const { reportId } = useParams();
    const routeReport = location.state?.report;
    const initialReport = useMemo(
        () => mergeInitialReportWithStoredCopy(routeReport, loadDiagnosticReport(reportId)),
        [reportId, routeReport]
    );
    const [report, setReport] = useState(initialReport);
    const [activeCauseIndex, setActiveCauseIndex] = useState(0);
    const [openToolsByCause, setOpenToolsByCause] = useState({});
    const [confirmedToolsByCause, setConfirmedToolsByCause] = useState({});
    const [userOwnedTools, setUserOwnedTools] = useState([]);
    const [isShopPreviewOpen, setIsShopPreviewOpen] = useState(false);
    const [copyNotice, setCopyNotice] = useState('');
    const [closeNotice, setCloseNotice] = useState('');
    const [isClosingTicket, setIsClosingTicket] = useState(false);
    const [showForumModal, setShowForumModal] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [garageProgressRequests, setGarageProgressRequests] = useState([]);
    const [isGarageProgressLoading, setIsGarageProgressLoading] = useState(false);
    const [garageProgressError, setGarageProgressError] = useState('');
    const [referralLock, setReferralLock] = useState(null);
    useEffect(() => {
        setReport(normalizeDiagnosticReportForUi(initialReport));
    }, [initialReport]);

    // Fetch the user's owned tools once so we can cross-check required tools.
    // Also pre-populates confirmedToolsByCause so owned tools appear pre-checked
    // on every diagnostic report that lists them as required.
    useEffect(() => {
        const identity = authState?.identity;
        if (!identity) return;
        let cancelled = false;
        fetch(`${getUserDetailsBaseUrl()}/api/tools/${encodeURIComponent(identity)}`)
            .then((r) => r.json())
            .then((data) => {
                if (cancelled || !Array.isArray(data?.tools)) return;
                const tools = data.tools;
                setUserOwnedTools(tools);

                // Pre-check any required tool that the user already owns,
                // across all likely causes in this report.
                const ownedNamesLower = new Set(tools.map((t) => t.name.toLowerCase()));
                setConfirmedToolsByCause((prev) => {
                    const next = { ...prev };
                    (report?.likelyCauses || []).forEach((cause) => {
                        const causeTools = cause?.requiredTools || [];
                        if (!causeTools.length) return;
                        const causeMap = { ...(next[cause.id] || {}) };
                        causeTools.forEach((tool) => {
                            // Only auto-check if not already explicitly set by user this session
                            if (ownedNamesLower.has(tool.name.toLowerCase())) {
                                causeMap[tool.name] = true;
                            }
                        });
                        next[cause.id] = causeMap;
                    });
                    return next;
                });
            })
            .catch(() => { /* silent — checklist still works without this data */ });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState?.identity]);

    const likelyCauses = useMemo(() => report?.likelyCauses || [], [report]);
    const activeCause = likelyCauses[activeCauseIndex] || null;
    const activeSymptoms = report?.symptomsDetailed?.length ? report.symptomsDetailed : report?.symptoms || [];
    const recommendedServiceLabel = useMemo(() => getRecommendedServiceLabel(activeCause), [activeCause]);
    const hideCostEstimates = Boolean(report?.hideCostEstimates || report?.shopReadyReport || report?.costEstimatesRedacted);
    const customerName = displayName || authState?.displayName || report?.customerName || 'CarFido Customer';
    const customerPhone = formatPhone(authState?.phone || report?.customerPhone || '');
    // Real account email first (authoritative for the driver viewing their own report); never a
    // fabricated "@carfido.com" placeholder. Fall back to the snapshot's email only if it's a
    // real address, else leave empty so the UI shows "No email provided".
    const reportSnapshotEmail = String(report?.customerEmail || '').trim();
    const customerEmail = String(authState?.email || '').trim()
        || (reportSnapshotEmail && !isPlaceholderCustomerEmail(reportSnapshotEmail) ? reportSnapshotEmail : '');
    const customerEmailDisplay = customerEmail || 'No email provided';
    const customerContactLine = [customerPhone, customerEmailDisplay].filter(Boolean).join(' · ');
    const customerComplaint = report?.customerComplaint || report?.mechanicSummary || report?.issue;
    const shopLikelyCauses = likelyCauses.slice(0, 3);
    // Reports saved before the completed-check shape was unified store their
    // checks as { checkTitle, outcome, completedAt }. Map them onto the fields
    // this page renders (recommendedCheck / outcomeLabel / summary / updatedAt)
    // so older reports display correctly without needing to be re-saved.
    const GUIDED_OUTCOME_LABELS = {
        normal: 'Result was normal / as expected',
        issue: 'Found an issue / result was abnormal',
        not_sure: "Not sure / couldn't complete",
    };
    const completedGuidedChecks = (Array.isArray(report?.completedGuidedChecks) ? report.completedGuidedChecks : [])
        .map((check) => ({
            ...check,
            recommendedCheck: check.recommendedCheck || check.checkTitle || check.causeTitle || 'Diagnostic check',
            outcomeLabel: check.outcomeLabel || GUIDED_OUTCOME_LABELS[check.outcome] || '',
            summary: check.summary || check.causeTitle || '',
            updatedAt: check.updatedAt || check.completedAt || '',
        }));
    const confirmedActiveCheck = completedGuidedChecks.find(
        (check) => check.causeId === activeCause?.id && check.outcome === 'issue'
    );
    const recommendedChecks = likelyCauses
        .slice(0, 4)
        .map((cause) => {
            const checkDetail = cause.expectedResult
                ? `${cause.recommendedCheck}: ${cause.expectedResult}`
                : cause.recommendedCheck;
            return checkDetail;
        });
    const reportIdentifier = report?.id || 'Unavailable';
    const reportGeneratedLabel = report?.createdLabel || 'Unavailable';
    const confirmedTools = confirmedToolsByCause[activeCause?.id] || {};
    const requiredTools = (activeCause?.requiredTools || []).map((tool) => {
        const obj = typeof tool === 'string' ? { name: tool } : { ...tool };
        let name = String(obj.name || '').trim();
        // Optional if the backend flagged it or the name ends with "(optional)".
        let optional = Boolean(obj.optional);
        if (/\(optional\)\s*$/i.test(name)) {
            optional = true;
            name = name.replace(/\s*\(optional\)\s*$/i, '').trim();
        }
        return { ...obj, name, optional };
    }).filter((tool) => tool.name);
    const essentialTools = requiredTools.filter((tool) => !tool.optional);
    // Parts needed to actually FIX this cause (e.g. the failed component to replace). The
    // section is labelled "Tools & parts" but previously only rendered tools, so a repair
    // like "Replace the door-lock actuator" showed "0 needed" even though it needs the part.
    const requiredParts = (activeCause?.requiredParts || []).map((part) => {
        const obj = typeof part === 'string' ? { name: part } : { ...part };
        return { ...obj, name: String(obj.name || '').trim(), notes: String(obj.notes || '').trim() };
    }).filter((part) => part.name);
    // Tools are informational — the user does NOT have to confirm them to begin the check.
    const allToolsConfirmed = true;
    const toolsExpanded = openToolsByCause[activeCause?.id] ?? true;
    const matchingTicket = useMemo(() => {
        const candidates = activeDiagnosticTicket
            ? [activeDiagnosticTicket, ...recentDiagnostics]
            : recentDiagnostics;
        return findDiagnosticTicketForReport(report, candidates);
    }, [report, activeDiagnosticTicket, recentDiagnostics]);
    // Evidence media (photos/videos the user uploaded while diagnosing / via "Found an Issue"),
    // pulled from the matched ticket. Each image loads via the stable diagnostic-evidence
    // endpoint, which re-signs the S3 URL per request so it never expires.
    const evidenceMedia = useMemo(() => {
        const phone = authState?.identity;
        if (!phone) return [];
        // Attachments are carried on the ticket-LIST payload (recentDiagnostics). The /active
        // endpoint omits them, so if the matched ticket has none, resolve the same ticket from
        // recentDiagnostics by id and use its attachments.
        let atts = Array.isArray(matchingTicket?.attachments) ? matchingTicket.attachments : [];
        if (!atts.length) {
            const ticketId = getRealTicketId(matchingTicket) || getReportTicketId(report);
            if (ticketId) {
                const fromList = (recentDiagnostics || []).find(
                    (t) => String(getRealTicketId(t) || '') === String(ticketId)
                );
                if (fromList && Array.isArray(fromList.attachments)) atts = fromList.attachments;
            }
        }
        atts = (atts || []).filter(Boolean);
        if (atts.length === 0) return [];
        const base = getUserDetailsBaseUrl();
        return atts
            .filter((a) => a && a.attachment_id)
            .map((a) => ({
                ...a,
                url: `${base}/api/diagnostic-evidence/${encodeURIComponent(phone)}/${a.attachment_id}`,
                isImage: a.media_kind === 'photo' || a.type === 'photo' || String(a.mime_type || '').startsWith('image/'),
                isVideo: a.media_kind === 'video' || a.type === 'video' || String(a.mime_type || '').startsWith('video/'),
            }));
    }, [matchingTicket, recentDiagnostics, report, authState?.identity]);
    // DIY repairs the user actually performed in the chat flow, with the steps from the
    // matching cause so the report shows what was done, not just what was recommended.
    const performedDiySteps = useMemo(() => {
        const guides = Array.isArray(report?.completedRepairGuides) ? report.completedRepairGuides : [];
        return guides.filter(Boolean).map((guide) => {
            const cause = likelyCauses.find((c) => c.id === guide.causeId);
            const steps = Array.isArray(cause?.repairSteps) && cause.repairSteps.length
                ? cause.repairSteps
                : (Array.isArray(cause?.steps) ? cause.steps : []);
            return { ...guide, steps };
        });
    }, [report, likelyCauses]);
    const garageProgressTicketId = useMemo(() => {
        const candidate = getRealTicketId(matchingTicket) || getReportTicketId(report);
        return /^\d+$/.test(String(candidate || '')) ? String(candidate) : '';
    }, [matchingTicket, report]);

    useEffect(() => {
        if (!authState?.identity || !garageProgressTicketId) {
            setReferralLock(null);
            return undefined;
        }

        let cancelled = false;
        listPartnerShops({ diagnosticTicketId: garageProgressTicketId })
            .then((shops) => {
                if (!cancelled) setReferralLock(shops?.referralLock || null);
            })
            .catch(() => {
                if (!cancelled) setReferralLock(null);
            });

        return () => {
            cancelled = true;
        };
    }, [authState?.identity, garageProgressTicketId]);

    useEffect(() => {
        if (!garageProgressTicketId || !garageProgressRequests.length) return;
        const released = garageProgressRequests.some((request) => (
            REFERRAL_LOCK_RELEASE_STATUSES.has(String(request?.request_status || '').toLowerCase())
        ));
        if (released) {
            setReferralLock(null);
        }
    }, [garageProgressRequests, garageProgressTicketId]);

    useEffect(() => {
        if (!authState?.identity || !garageProgressTicketId) {
            setGarageProgressRequests([]);
            setGarageProgressError('');
            setIsGarageProgressLoading(false);
            return undefined;
        }

        let cancelled = false;
        const loadGarageProgress = (silent = false) => {
            if (!silent) setIsGarageProgressLoading(true);
            listPartnerRepairRequests({ diagnostic_ticket_id: garageProgressTicketId })
                .then((requests) => {
                    if (cancelled) return;
                    setGarageProgressRequests(requests);
                    setGarageProgressError('');
                })
                .catch((error) => {
                    if (cancelled) return;
                    setGarageProgressError(error?.message || 'Could not load Garage progress.');
                    setGarageProgressRequests([]);
                })
                .finally(() => {
                    if (!cancelled && !silent) setIsGarageProgressLoading(false);
                });
        };

        loadGarageProgress(false);
        const intervalId = window.setInterval(() => loadGarageProgress(true), 6000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [authState?.identity, garageProgressTicketId]);

    useEffect(() => {
        if (!report || !matchingTicket) return;

        const reportClosed = isReportClosedStatus(report);
        const matchingTicketClosed = isTicketClosed(matchingTicket);
        // DIY checks are persisted on the ticket (diy_checks). Pull them into the
        // report when the client copy has none — this is what makes the completed
        // checks show in the Shop-Ready report after the ticket is closed and on
        // other devices, where only the server ticket carries them.
        const reportDiyChecks = Array.isArray(report.completedGuidedChecks) ? report.completedGuidedChecks : [];
        const ticketDiyChecks = Array.isArray(matchingTicket?.diy_checks) ? matchingTicket.diy_checks : [];
        const mergedDiyChecks = reportDiyChecks.length ? reportDiyChecks : ticketDiyChecks;
        const nextReport = {
            ...report,
            completedGuidedChecks: mergedDiyChecks,
            diagnosticTicketId: getRealTicketId(matchingTicket) || getReportTicketId(report),
            ticketNumber: getTicketDisplayNumber(matchingTicket) || report.ticketNumber || report.ticket_number,
            ticketStatus: reportClosed ? 'closed' : (matchingTicket?.status || matchingTicket?.ticketStatus || report.ticketStatus),
            ticketStage: reportClosed ? 'closed' : (matchingTicket?.stage || matchingTicket?.ticketStage || report.ticketStage),
            status: reportClosed || matchingTicketClosed ? 'Closed' : report.status,
        };

        if (
            mergedDiyChecks.length === reportDiyChecks.length &&
            String(nextReport.diagnosticTicketId || '') === String(report.diagnosticTicketId || '') &&
            String(nextReport.ticketNumber || '') === String(report.ticketNumber || '') &&
            String(nextReport.ticketStatus || '') === String(report.ticketStatus || '') &&
            String(nextReport.ticketStage || '') === String(report.ticketStage || '') &&
            String(nextReport.status || '') === String(report.status || '')
        ) {
            return;
        }

        setReport(nextReport);
        saveDiagnosticReport(nextReport);
    }, [report, matchingTicket]);

    useEffect(() => {
        const threadId = report?.threadId || matchingTicket?.thread_id || null;
        if (!authState?.identity || !threadId || !report?.id) return;

        let cancelled = false;

        const refreshReportFromBackend = async () => {
            try {
                const response = await fetch(
                    `${CHAT_API_BASE_URL}/secure/threads/${encodeURIComponent(threadId)}/diagnostic-report`,
                    {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                            ...getTesterModeHeaders(),
                        },
                        body: JSON.stringify({
                            vehicle_label: report.vehicle || null,
                            report_id: report.id,
                        }),
                    }
                );

                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data?.report || cancelled) return;
                announceTicketTitle(threadId, data);

                const reportClosed = isReportClosedStatus(report);
                const matchingTicketClosed = isTicketClosed(matchingTicket);
                // DIY progress (the guided checks and repair guides the user
                // completed in chat) lives only in the client copy — the backend
                // report doesn't carry it. Preserve it across the refresh so it
                // isn't wiped from the report view or from saved storage.
                const mergedCompletedChecks =
                    (Array.isArray(data.report.completedGuidedChecks) && data.report.completedGuidedChecks.length)
                        ? data.report.completedGuidedChecks
                        : (report.completedGuidedChecks || []);
                const mergedCompletedRepairs =
                    (Array.isArray(data.report.completedRepairGuides) && data.report.completedRepairGuides.length)
                        ? data.report.completedRepairGuides
                        : (report.completedRepairGuides || []);

                const mergedReport = normalizeDiagnosticReportForUi({
                    ...data.report,
                    completedGuidedChecks: mergedCompletedChecks,
                    completedRepairGuides: mergedCompletedRepairs,
                    diagnosticTicketId: getReportTicketId(report) || getRealTicketId(matchingTicket) || getReportTicketId(data.report),
                    ticketNumber: report.ticketNumber || getTicketDisplayNumber(matchingTicket) || data.report.ticketNumber || data.report.ticket_number,
                    ticketStatus: reportClosed ? 'closed' : (matchingTicket?.status || matchingTicket?.ticketStatus || report.ticketStatus || data.report.ticketStatus),
                    ticketStage: reportClosed ? 'closed' : (matchingTicket?.stage || matchingTicket?.ticketStage || report.ticketStage || data.report.ticketStage),
                    status: reportClosed || matchingTicketClosed ? 'Closed' : (report.status || data.report.status),
                    customerPhone: report.customerPhone || authState?.phone || data.report.customerPhone,
                    // Bake the REAL account email into the saved/shared snapshot — never a
                    // fabricated "@carfido.com" placeholder (so a shop later reading this snapshot
                    // sees the real address or none).
                    customerEmail: String(authState?.email || '').trim()
                        || [report.customerEmail, data.report.customerEmail]
                            .map((e) => String(e || '').trim())
                            .find((e) => e && !isPlaceholderCustomerEmail(e)) || '',
                    customerName: report.customerName || data.report.customerName,
                    threadId,
                });

                if (JSON.stringify(mergedReport) === JSON.stringify(report)) return;
                setReport(mergedReport);
                saveDiagnosticReport(mergedReport);
            } catch (error) {
                console.error('[Report] backend refresh failed', error);
            }
        };

        refreshReportFromBackend();

        return () => {
            cancelled = true;
        };
    }, [report, authState?.identity, matchingTicket]);

    const goBackToChat = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate('/chat');
    };

    const navigateToReportChat = (prompt, options = {}) => {
        const threadId = report?.threadId || report?.thread_id || getTicketThreadId(matchingTicket) || '';
        const intent = {
            q: prompt,
            clientIntent: options.clientIntent || null,
            suppressDiagnosticTicket: Boolean(options.suppressDiagnosticTicket),
            diagnosticReport: {
                reportId: report?.id || reportId,
                ticketId: getRealTicketId(matchingTicket) || getReportTicketId(report),
                threadId,
                issue: report?.issue,
                vehicle: report?.vehicle,
                activeCause: activeCause?.title || '',
            },
        };
        if (threadId && onSelectThread) {
            onSelectThread(threadId);
        }
        if (threadId && authState?.identity) {
            setStoredThreadId(authState.identity, threadId);
        }
        try {
            window.sessionStorage.setItem(PENDING_CHAT_INTENT_STORAGE_KEY, JSON.stringify(intent));
            window.localStorage.setItem(PENDING_CHAT_INTENT_STORAGE_KEY, JSON.stringify(intent));
        } catch {}
        navigate('/chat', {
            state: intent,
        });
    };

    const handleAskDash = () => {
        const issue = report?.issue || activeCause?.title || 'this diagnostic report';
        navigateToReportChat(`I have a question about my diagnostic report for ${issue}.`);
    };

    const handleFindPart = () => {
        const issue = activeCause?.title || report?.issue || 'this diagnostic issue';
        const vehicleContext = report?.vehicle ? ` for my ${report.vehicle}` : '';
        navigateToReportChat(`Find a part or tool for ${issue}${vehicleContext}.`, {
            clientIntent: 'parts',
            suppressDiagnosticTicket: true,
        });
    };

    // Open the Garage's "Find a Shop for This Issue" drawer (partner shops + Request Estimate)
    // for this report's ticket — the same shop-finder the chat's Find Shop and the Garage use —
    // instead of routing to a chat shop search.
    const handleFindShop = () => {
        const ticketId = getRealTicketId(matchingTicket) || getReportTicketId(report) || null;
        const threadId = report?.threadId || report?.thread_id || getTicketThreadId(matchingTicket) || null;
        // Persist the report snapshot so the drawer can attach it to the estimate request.
        if (report) {
            try { saveDiagnosticReport(normalizeDiagnosticReportForUi(report)); } catch (_) { /* noop */ }
        }
        navigate('/garage', {
            state: {
                openEstimateTicketId: ticketId,
                openEstimateThreadId: threadId,
                openShopReport: {
                    vehicle: report?.vehicle || '',
                    issue: report?.issue || '',
                    ticketId,
                },
            },
        });
    };

    const handleBackToGarage = () => {
        navigate('/garage');
    };

    const handleOpenGarageChat = (request) => {
        if (request?.request_id) {
            const directThreadId = (
                request?.source_repair_log_id ||
                request?.sourceRepairLogId ||
                request?.repair_log_id ||
                request?.repairLogId ||
                request?.thread_id ||
                request?.threadId ||
                ''
            );
            const matchedTicket = (recentDiagnostics || []).find((ticket) => (
                String(getRealTicketId(ticket) || '') === String(request?.diagnostic_ticket_id || request?.diagnosticTicketId || '') ||
                (directThreadId && String(getTicketThreadId(ticket) || '') === String(directThreadId))
            ));
            const linkedThreadId = String(directThreadId || getTicketThreadId(matchedTicket) || getTicketThreadId(matchingTicket) || '');
            if (linkedThreadId && onSelectThread) {
                onSelectThread(linkedThreadId);
            }
            navigate('/chat', { state: { openGarageRequestId: request.request_id } });
            return;
        }
        handleBackToGarage();
    };

    if (!report) {
        return <EmptyReport onBack={() => navigate('/chat')} />;
    }

    // Three-state safe-to-drive color, shared with the in-chat card: No → red,
    // Use caution → yellow, Yes → green.
    const safeLevel = getSafeToDriveLevel(report.safeToDrive);
    const safeStyle = SAFE_TO_DRIVE_STYLES[safeLevel];
    const closeTargetPhone = (
        authState?.identity ||
        getReportPhone(report) ||
        getTicketPhone(matchingTicket) ||
        getTicketPhone(activeDiagnosticTicket) ||
        getTicketPhone(recentDiagnostics?.find((ticket) => !isTicketClosed(ticket))) ||
        getTicketPhone(recentDiagnostics?.[0]) ||
        ''
    );
    const closeableTicketId = getRealTicketId(matchingTicket) || getReportTicketId(report);
    const reportClosed = isReportClosedStatus(report);
    const liveTicketStatus = String(matchingTicket?.status || matchingTicket?.ticketStatus || '').toLowerCase();
    const cachedTicketStatus = String(report.ticketStatus || '').toLowerCase();
    const ticketStatus = reportClosed ? 'closed' : (liveTicketStatus || cachedTicketStatus);
    const ticketClosed = reportClosed || ticketStatus === 'closed' || ticketStatus === 'abandoned';
    const ticketNumber = report.ticketNumber || getTicketDisplayNumber(matchingTicket) || getRealTicketId(matchingTicket) || '';
    const canCloseTicket = !ticketClosed;
    const findShopActionLabel = referralLock
        ? `Continue with ${referralLock.shop?.name || 'your shop'}`
        : 'Find Shop';

    const toggleTool = (causeId, toolName) => {
        const wasChecked = Boolean((confirmedToolsByCause[causeId] || {})[toolName]);
        const nowChecked = !wasChecked;

        // 1. Update local UI immediately (optimistic)
        setConfirmedToolsByCause((prev) => ({
            ...prev,
            [causeId]: {
                ...(prev[causeId] || {}),
                [toolName]: nowChecked,
            },
        }));

        // 2. Persist to / remove from the DB
        const identity = authState?.identity;
        if (!identity) return;

        if (nowChecked) {
            // User just checked the box → add to inventory (ignore duplicate errors)
            fetch(`${getUserDetailsBaseUrl()}/api/tools`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number: identity, name: toolName }),
            })
                .then((r) => r.json())
                .then((data) => {
                    // Update userOwnedTools so the badge reflects immediately
                    if (data?.tool) {
                        setUserOwnedTools((prev) => {
                            const alreadyHas = prev.some(
                                (t) => t.name.toLowerCase() === toolName.toLowerCase()
                            );
                            return alreadyHas ? prev : [...prev, data.tool];
                        });
                    }
                })
                .catch(() => { /* silent — badge will refresh on next page load */ });
        } else {
            // User unchecked the box → remove from inventory
            const ownedTool = userOwnedTools.find(
                (t) => t.name.toLowerCase() === toolName.toLowerCase()
            );
            if (ownedTool?.tool_id) {
                fetch(
                    `${getUserDetailsBaseUrl()}/api/tools/${ownedTool.tool_id}?phone_number=${encodeURIComponent(identity)}`,
                    { method: 'DELETE' }
                )
                    .then((r) => r.json())
                    .then((data) => {
                        if (data?.tool_id) {
                            setUserOwnedTools((prev) =>
                                prev.filter((t) => t.tool_id !== data.tool_id)
                            );
                        }
                    })
                    .catch(() => { /* silent */ });
            }
        }
    };

    const handleContinueCheck = () => {
        if (!activeCause || !allToolsConfirmed || ticketClosed) return;
        const guidedReport = normalizeDiagnosticReportForUi({
            ...report,
            threadId: report.threadId || matchingTicket?.thread_id || null,
            diagnosticTicketId: getRealTicketId(matchingTicket) || getReportTicketId(report) || report.diagnosticTicketId || null,
            ticketNumber: report.ticketNumber || getTicketDisplayNumber(matchingTicket),
            customerPhone: authState?.phone || getReportPhone(report),
        });
        saveDiagnosticReport(guidedReport);
        const threadId = report.threadId || matchingTicket?.thread_id || null;
        const intent = {
            guidedCheck: {
                report: guidedReport,
                reportId: guidedReport.id,
                causeId: activeCause.id,
                causeIndex: activeCauseIndex,
                threadId,
            },
        };
        try {
            window.sessionStorage.setItem(PENDING_CHAT_INTENT_STORAGE_KEY, JSON.stringify(intent));
            window.localStorage.setItem(PENDING_CHAT_INTENT_STORAGE_KEY, JSON.stringify(intent));
        } catch {}
        if (threadId && authState?.identity) {
            setStoredThreadId(authState.identity, threadId);
        }
        const targetPath = `/chat?guidedCheck=${Date.now()}`;
        navigate(targetPath, {
            state: intent,
        });
    };

    const buildShopSummaryText = () => {
        const lines = [
            `Report ID: ${reportIdentifier}`,
            `Generated: ${reportGeneratedLabel}`,
            '',
            `Owner / Customer: ${customerName}`,
            customerContactLine,
            '',
            `Vehicle: ${report.vehicle}`,
            '',
            `Customer Complaint: ${customerComplaint}`,
            ...(report.mechanicSummary && report.mechanicSummary !== customerComplaint
                ? ['', 'Shop-Ready Summary:', report.mechanicSummary]
                : []),
            '',
            'Observed Symptoms:',
            ...activeSymptoms.map((symptom) => `• ${symptom}`),
            '',
            'Likely Causes:',
            ...shopLikelyCauses.map((cause, index) => `${index + 1}. ${cause.title}`),
            '',
            ...(completedGuidedChecks.length > 0
                ? [
                    'Completed Diagnostic Checks:',
                    ...completedGuidedChecks.map((check) => {
                        const noteSuffix = check.notes ? ` Notes: ${check.notes}` : '';
                        return `• ${check.recommendedCheck}: ${check.outcomeLabel}. ${check.summary}${noteSuffix}`;
                    }),
                    '',
                ]
                : []),
            ...(performedDiySteps.length > 0
                ? [
                    'Performed DIY Steps:',
                    ...performedDiySteps.flatMap((guide) => [
                        `${guide.repairTitle || guide.causeTitle || 'DIY repair'} (completed):`,
                        ...guide.steps.map((step, si) => `  ${si + 1}. ${step.instruction || step.title || 'Step'}${step.detail ? `: ${step.detail}` : ''}`),
                        ...(guide.note ? [`  Notes: ${guide.note}`] : []),
                        ...(guide.mileage ? [`  Odometer: ${guide.mileage}`] : []),
                    ]),
                    '',
                ]
                : []),
            ...(evidenceMedia.length > 0
                ? [
                    'Evidence:',
                    ...evidenceMedia.map((m) => `• ${m.caption || m.name || m.original_filename || 'Attachment'}`),
                    '',
                ]
                : []),
            'Recommended Checks:',
            ...recommendedChecks.map((check) => `• ${check}`),
        ];
        return lines.join('\n');
    };

    const handleCopySummary = async () => {
        const text = buildShopSummaryText();
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                setCopyNotice('Summary copied');
            } else {
                setCopyNotice('Clipboard not available');
            }
        } catch {
            setCopyNotice('Unable to copy summary');
        }
        setTimeout(() => setCopyNotice(''), 2000);
    };

    const handleShareReport = async () => {
        const text = buildShopSummaryText();
        try {
            if (navigator?.share) {
                await navigator.share({
                    title: 'Shop-Ready Report',
                    text,
                });
                return;
            }
        } catch {}
        await handleCopySummary();
    };

    // Download the shop-ready report as a document. We render a clean, self-contained,
    // print-styled HTML version and open the browser print dialog so the mechanic gets a
    // proper PDF (no extra libraries or backend round-trip needed).
    const handleDownloadReport = () => {
        const esc = (value) => String(value ?? '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const section = (title, bodyHtml) => (
            `<h2>${esc(title)}</h2>${bodyHtml}`
        );
        const listHtml = (items) => `<ul>${items.map((i) => `<li>${i}</li>`).join('')}</ul>`;
        const symptomsHtml = listHtml(activeSymptoms.map(esc));
        const causesHtml = `<ol>${shopLikelyCauses.map((c) => `<li>${esc(c.title)}</li>`).join('')}</ol>`;
        const checksHtml = completedGuidedChecks.length > 0
            ? listHtml(completedGuidedChecks.map((check) => {
                const noteSuffix = check.notes ? ` Notes: ${esc(check.notes)}` : '';
                return `<strong>${esc(check.recommendedCheck)}:</strong> ${esc(check.outcomeLabel)}. ${esc(check.summary)}${noteSuffix}`;
            }))
            : '';
        const diyHtml = performedDiySteps.length > 0
            ? performedDiySteps.map((guide) => {
                const stepsHtml = guide.steps.length > 0
                    ? `<ol>${guide.steps.map((s) => (
                        `<li><strong>${esc(s.instruction || s.title || 'Step')}</strong>${s.detail ? `<div class="muted">${esc(s.detail)}</div>` : ''}</li>`
                    )).join('')}</ol>`
                    : '';
                const meta = [];
                if (guide.note) meta.push(`<div class="muted">Notes: ${esc(guide.note)}</div>`);
                if (guide.mileage) meta.push(`<div class="muted">Odometer: ${esc(guide.mileage)}</div>`);
                return `<div class="box"><div class="name" style="font-size:15px">${esc(guide.repairTitle || guide.causeTitle || 'DIY repair')} <span class="muted">(completed)</span></div>${stepsHtml}${meta.join('')}</div>`;
            }).join('')
            : '';
        // The print iframe is about:blank, so relative URLs (dev mode) won't resolve — make
        // the evidence image URLs absolute against the current origin.
        const absoluteUrl = (u) => {
            const s = String(u || '');
            if (!s || /^https?:\/\//i.test(s)) return s;
            const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
            return `${origin}${s.startsWith('/') ? '' : '/'}${s}`;
        };
        const evidenceHtml = evidenceMedia.length > 0
            ? `<div class="evidence">${evidenceMedia.map((m) => {
                const label = esc(m.caption || m.name || m.original_filename || 'Evidence');
                return m.isImage
                    ? `<figure class="evi"><img src="${esc(absoluteUrl(m.url))}" alt="${label}" /></figure>`
                    : `<figure class="evi evi-file"><figcaption>${m.isVideo ? 'Video' : 'File'}: ${label}</figcaption></figure>`;
            }).join('')}</div>`
            : '';
        const recHtml = listHtml(recommendedChecks.map(esc));
        const generatedOn = new Date().toLocaleString();
        const html = `<!doctype html><html lang="en"><head><meta charset="utf-8" />
<title>Shop-Ready Report | ${esc(report.vehicle)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #414141; margin: 40px; line-height: 1.5; }
  .brand { font-size: 12px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: #414141; }
  h1 { font-size: 24px; margin: 4px 0 2px; }
  .sub { color: #414141; font-size: 13px; margin-bottom: 20px; }
  h2 { font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #414141; margin: 22px 0 8px; }
  .box { border: 1px solid #d9d9d9; border-radius: 12px; padding: 14px 16px; margin-bottom: 6px; }
  .name { font-size: 18px; font-weight: 800; }
  .muted { color: #414141; font-size: 13px; }
  .vehicle { font-size: 18px; font-weight: 800; }
  .complaint { font-size: 16px; font-weight: 700; }
  ul, ol { margin: 4px 0 0; padding-left: 22px; }
  li { margin: 4px 0; }
  .evidence { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 4px; }
  .evi { margin: 0; width: 150px; }
  .evi img { width: 150px; height: 150px; object-fit: cover; border: 1px solid #d9d9d9; border-radius: 8px; display: block; }
  .evi-file { display: flex; align-items: center; justify-content: center; height: 150px; border: 1px solid #d9d9d9; border-radius: 8px; padding: 8px; text-align: center; }
  .evi-file figcaption { color: #414141; font-size: 12px; font-weight: 700; word-break: break-word; }
  .footer { margin-top: 28px; border-top: 1px solid #ebebeb; padding-top: 10px; color: #414141; font-size: 11px; }
  @media print { body { margin: 16mm; } .box, .evi, figure { break-inside: avoid; } img { break-inside: avoid; } }
</style></head><body>
  <div class="brand">CarFido · Shop-Ready Report</div>
  <h1>${esc(report.vehicle)}</h1>
  <div class="sub">Symptoms, likely causes, evidence, and recommended checks</div>
  ${section('Report Info', `<div class="box"><div><strong>Report ID:</strong> ${esc(reportIdentifier)}</div><div><strong>Generated:</strong> ${esc(reportGeneratedLabel)}</div></div>`)}
  ${section('Owner / Customer', `<div class="box"><div class="name">${esc(customerName)}</div><div class="muted">${esc(customerContactLine)}</div></div>`)}
  ${section('Vehicle', `<div class="vehicle">${esc(report.vehicle)}</div>`)}
  ${section('Customer Complaint', `<div class="complaint">${esc(customerComplaint)}</div>`)}
  ${report.mechanicSummary && report.mechanicSummary !== customerComplaint ? section('Shop-Ready Summary', `<div class="complaint">${esc(report.mechanicSummary)}</div>`) : ''}
  ${section('Observed Symptoms', symptomsHtml)}
  ${section('Likely Causes', causesHtml)}
  ${checksHtml ? section('Completed Diagnostic Checks', checksHtml) : ''}
  ${diyHtml ? section('Performed DIY Steps', diyHtml) : ''}
  ${evidenceHtml ? section('Evidence', evidenceHtml) : ''}
  ${section('Recommended Checks', recHtml)}
  <div class="footer">Generated ${esc(reportGeneratedLabel || generatedOn)} · CarFido</div>
</body></html>`;

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        document.body.appendChild(iframe);
        const cleanup = () => { try { document.body.removeChild(iframe); } catch {} };
        try {
            const doc = iframe.contentWindow?.document;
            if (!doc) { cleanup(); setCopyNotice('Unable to prepare download'); setTimeout(() => setCopyNotice(''), 2000); return; }
            doc.open();
            doc.write(html);
            doc.close();
            iframe.contentWindow.focus();
            // Wait for evidence images to finish loading before opening the print dialog —
            // otherwise the PDF captures blank/broken images. Falls back after 6s if any stall.
            const triggerPrint = () => {
                try { iframe.contentWindow.print(); } catch {}
                setTimeout(cleanup, 1000);
            };
            const imgs = Array.from(doc.images || []);
            if (imgs.length === 0) {
                setTimeout(triggerPrint, 300);
            } else {
                let printed = false;
                const runOnce = () => { if (printed) return; printed = true; setTimeout(triggerPrint, 150); };
                let remaining = imgs.length;
                const onOne = () => { remaining -= 1; if (remaining <= 0) runOnce(); };
                imgs.forEach((img) => {
                    if (img.complete) { onOne(); return; }
                    img.addEventListener('load', onOne);
                    img.addEventListener('error', onOne);
                });
                // Safety net: print anyway if some image never resolves.
                setTimeout(runOnce, 6000);
            }
        } catch {
            cleanup();
            setCopyNotice('Unable to download report');
            setTimeout(() => setCopyNotice(''), 2000);
        }
    };

    const handleCloseTicket = async () => {
        if (ticketClosed || isClosingTicket) return;

        setIsClosingTicket(true);
        setCloseNotice('');
        try {
            const resolvedTicket = await resolveCloseableTicketForReport({
                report,
                matchingTicket,
                activeDiagnosticTicket,
                recentDiagnostics,
                refreshDiagnosticTickets,
                phone: closeTargetPhone,
            });
            const effectiveCloseTargetPhone = closeTargetPhone || getTicketPhone(resolvedTicket);
            const ticketId = getRealTicketId(resolvedTicket) || closeableTicketId;
            const closeSummary = `Ticket closed from diagnostic report for issue: ${report.issue}`;

            if (!effectiveCloseTargetPhone) {
                throw new Error('Ticket details are missing from this report.');
            }

            let closedTicket = null;
            if (ticketId) {
                const response = await fetch(
                    `${getUserDetailsBaseUrl()}/api/diagnostic-tickets/${encodeURIComponent(effectiveCloseTargetPhone)}/${encodeURIComponent(ticketId)}`,
                    {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            stage: 'closed',
                            status: 'closed',
                            outcome: 'successful',
                            summary: closeSummary,
                            diy_checks: Array.isArray(report?.completedGuidedChecks) ? report.completedGuidedChecks : [],
                        }),
                    }
                );
                if (response.ok) {
                    closedTicket = await response.json();
                } else if (response.status === 404) {
                    closedTicket = await closeMatchingTicketForReport({
                        phone: effectiveCloseTargetPhone,
                        report,
                        resolvedTicket,
                        summary: closeSummary,
                    });
                } else {
                    let errorMessage = 'Failed to close ticket';
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData?.detail || errorData?.error || errorMessage;
                    } catch {}
                    throw new Error(errorMessage);
                }
            } else {
                closedTicket = await closeMatchingTicketForReport({
                    phone: effectiveCloseTargetPhone,
                    report,
                    resolvedTicket,
                    summary: closeSummary,
                });
            }

            const nextReport = {
                ...report,
                diagnosticTicketId: closedTicket?.diagnostic_ticket_id || ticketId,
                ticketNumber: report.ticketNumber || getTicketDisplayNumber(resolvedTicket) || closedTicket?.diagnostic_ticket_id,
                ticketStatus: closedTicket?.status || 'closed',
                ticketStage: closedTicket?.stage || 'closed',
                status: 'Closed',
                customerPhone: effectiveCloseTargetPhone,
            };
            setReport(nextReport);
            saveDiagnosticReport(nextReport);
            const closedThreadId = getTicketThreadId(resolvedTicket) || report.threadId || report.thread_id || '';
            window.dispatchEvent(new CustomEvent('dash_active_diagnostic_ticket', {
                detail: {
                    ...(resolvedTicket || {}),
                    ...(closedTicket || {}),
                    diagnostic_ticket_id: closedTicket?.diagnostic_ticket_id || ticketId,
                    display_ticket_number: getTicketDisplayNumber(resolvedTicket) || report.ticketNumber || '',
                    ticket_number: resolvedTicket?.ticket_number || closedTicket?.ticket_number || ticketId,
                    thread_id: closedThreadId,
                    source_repair_log_id: closedThreadId || getTicketThreadId(resolvedTicket),
                    title: getTicketTitle(resolvedTicket) || report.issue,
                    symptom_summary: resolvedTicket?.symptom_summary || report.issue,
                    vehicle_label: getTicketVehicleLabel(resolvedTicket) || report.vehicle || '',
                    vehicleLabel: getTicketVehicleLabel(resolvedTicket) || report.vehicle || '',
                    phone_number: effectiveCloseTargetPhone,
                    stage: closedTicket?.stage || 'closed',
                    status: closedTicket?.status || 'closed',
                    outcome: closedTicket?.outcome || 'successful',
                },
            }));
            await refreshDiagnosticTickets?.();
            setCloseNotice('Ticket resolved');
        } catch (error) {
            setCloseNotice(error?.message || 'Unable to resolve ticket');
        } finally {
            setIsClosingTicket(false);
            setTimeout(() => setCloseNotice(''), 2500);
        }
    };

    return (
        <div className="h-full w-full min-w-0 overflow-y-auto bg-[#fafafa] custom-scrollbar">
            <div className="sticky top-0 z-40 border-b border-[#d9d9d9] bg-[#fafafa] px-3 py-2.5 shadow-sm md:px-6 md:py-3">
                <div className="mx-auto flex max-w-4xl min-w-0 flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-4">
                    <div className="flex min-w-0 items-center gap-2.5 md:gap-3">
                        <button
                            type="button"
                            onClick={goBackToChat}
                            aria-label="Back to chat"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#414141] transition-all hover:bg-[#f2f2f2] hover:text-[#414141] md:-ml-1"
                        >
                            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="m12 19-7-7 7-7" />
                            </svg>
                        </button>
                        <div className="min-w-0">
                            <h1 className="truncate text-[16px] font-black leading-tight text-[#414141] md:text-xl">
                                Symptom Report
                            </h1>
                            <p className="truncate text-xs font-semibold text-[#414141] md:text-sm">{report.vehicle}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                {ticketNumber && (
                                    <span className="rounded-full bg-[#f5f5f5] px-2.5 py-1 text-[11px] font-black text-[#414141]">
                                        Ticket #{ticketNumber}
                                    </span>
                                )}
                                {closeNotice && (
                                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-black ${
                                        ticketClosed ? 'bg-[#eefbf3] text-[#0a8f53]' : 'bg-[#fff7e8] text-[#b7791f]'
                                    }`}>
                                        {closeNotice}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="-mx-0.5 flex min-w-0 gap-2 overflow-x-auto pb-1 md:mx-0 md:flex-wrap md:items-center md:justify-end md:overflow-visible md:pb-0">
                        <button
                            type="button"
                            onClick={handleAskDash}
                            className="inline-flex min-h-[34px] shrink-0 items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-3 text-[12px] font-black text-[#414141] transition-all hover:border-[#414141] hover:bg-[#f5f5f5] md:min-h-[36px] md:text-sm"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></svg>
                            Ask CarFido
                        </button>
                        <button
                            type="button"
                            onClick={handleFindPart}
                            className="inline-flex min-h-[34px] shrink-0 items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-3 text-[12px] font-black text-[#414141] transition-all hover:border-[#414141] hover:bg-[#f5f5f5] md:min-h-[36px] md:text-sm"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 7h10v10H7z" /><path d="M4 10h3M17 10h3M4 14h3M17 14h3M10 4v3M14 4v3M10 17v3M14 17v3" /></svg>
                            Find Part
                        </button>
                        <button
                            type="button"
                            onClick={handleFindShop}
                            className="inline-flex min-h-[34px] shrink-0 items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-3 text-[12px] font-black text-[#414141] transition-all hover:border-[#414141] hover:bg-[#f5f5f5] md:min-h-[36px] md:text-sm"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9 5 3h14l2 6" /><path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M9 22V12h6v10" /></svg>
                            {findShopActionLabel}
                        </button>
                        <button
                            type="button"
                            onClick={handleBackToGarage}
                            className="inline-flex min-h-[34px] shrink-0 items-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-3 text-[12px] font-black text-[#414141] transition-all hover:border-[#414141] hover:bg-[#f5f5f5] md:min-h-[36px] md:text-sm"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" /><circle cx="7" cy="17" r="2" /><path d="M9 17h6" /><circle cx="17" cy="17" r="2" /></svg>
                            Garage
                        </button>
                        <span className={`inline-flex min-h-[34px] shrink-0 items-center rounded-full px-3 py-1 text-[11px] font-black ${
                            ticketClosed ? 'bg-[#eefbf3] text-[#0a8f53]' : 'bg-[#f1f1f1] text-[#414141]'
                        }`}>
                            {ticketClosed ? 'Resolved' : 'Open'}
                        </span>
                        <button
                            type="button"
                            onClick={handleCloseTicket}
                            disabled={!canCloseTicket || isClosingTicket}
                            className={`min-h-[34px] shrink-0 rounded-full px-3 py-2 text-[12px] font-black transition-all md:px-4 md:text-sm ${
                                !canCloseTicket
                                    ? 'cursor-not-allowed border border-[#d9d9d9] bg-white text-[#afafaf]'
                                    : 'bg-[#414141] text-white hover:bg-black'
                            }`}
                        >
                            {ticketClosed ? 'Ticket Resolved' : isClosingTicket ? 'Resolving...' : 'Resolve Ticket'}
                        </button>
                    </div>
                </div>
            </div>

            <main className="mx-auto w-full max-w-4xl min-w-0 px-3 pb-6 pt-4 sm:px-4 md:px-6 md:pb-10 md:pt-5">
                <SectionTitle>Can I Drive?</SectionTitle>
                <section className="overflow-hidden rounded-2xl border shadow-sm sm:rounded-[28px]" style={{ borderColor: safeStyle.border, backgroundColor: safeStyle.bg }}>
                    <div className="flex items-center justify-between gap-3 border-b px-4 py-3.5 sm:px-5 sm:py-4" style={{ borderColor: safeStyle.border }}>
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white" style={{ color: safeStyle.icon }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    <path d="M12 8v4" />
                                    <path d="M12 16h.01" />
                                </svg>
                            </div>
                            <span className="text-[14px] font-black text-[#414141] sm:text-[15px]">Status:</span>
                        </div>
                        <span className="shrink-0 rounded-full px-3 py-1 text-xs font-black sm:text-sm" style={{ backgroundColor: safeStyle.pillBg, color: safeStyle.pillText }}>
                            {report.safeToDrive}
                        </span>
                    </div>
                    <div className="px-4 py-4 sm:px-5">
                        <p className="text-[14px] font-semibold leading-relaxed sm:text-base" style={{ color: safeStyle.body }}>
                            {report.canDriveMessage || report.safetyNote}
                        </p>
                        {report.canDriveTimeframe && String(report.canDriveTimeframe).trim().toLowerCase() !== 'n/a' && (
                            <p className="mt-2 text-sm font-bold" style={{ color: safeStyle.sub }}>
                                {report.canDriveTimeframe}
                            </p>
                        )}
                    </div>
                </section>

                <SectionTitle>Symptom Summary</SectionTitle>
                <section className="overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm sm:rounded-[28px]">
                    <div className="border-b border-[#d9d9d9] px-4 py-4 sm:px-5 sm:py-5">
                        <div className="grid gap-5 md:grid-cols-[1fr_auto]">
                            <div>
                                <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Issue</p>
                                <p className="mt-2 text-[17px] font-black leading-snug text-[#414141] sm:text-[20px]">{report.issue}</p>
                            </div>
                            <div className="min-w-0 md:min-w-[260px]">
                                <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Symptoms</p>
                                <ul className="mt-2 space-y-1.5 text-[14px] font-medium text-[#414141] sm:text-[15px]">
                                    {activeSymptoms.map((symptom) => (
                                        <li key={symptom} className="flex items-start gap-2">
                                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#8C8C8C]" />
                                            <span>{symptom}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {activeCause && (
                        <>
                            <div className="border-b border-[#d9d9d9] bg-[#F9F9F9] px-5 py-4">
                                <div className="flex items-center justify-between gap-3">
                                    <InfoBadge className="bg-[#f1f1f1] text-[#414141]">
                                        {activeCause.kindLabel}
                                    </InfoBadge>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-semibold text-[#414141]">
                                            {activeCauseIndex + 1} of {likelyCauses.length}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <ChevronButton
                                                direction="previous"
                                                onClick={() => setActiveCauseIndex((index) => Math.max(index - 1, 0))}
                                                disabled={activeCauseIndex === 0}
                                            />
                                            <ChevronButton
                                                direction="next"
                                                onClick={() => setActiveCauseIndex((index) => Math.min(index + 1, likelyCauses.length - 1))}
                                                disabled={activeCauseIndex === likelyCauses.length - 1}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border-b border-[#d9d9d9] px-5 py-4">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div className="flex items-center gap-2">
                                        {likelyCauses.map((cause, index) => (
                                            <span
                                                key={cause.id}
                                                className={`h-3 w-3 rounded-full ${index === activeCauseIndex ? 'bg-[#D81F26]' : 'bg-[#e5e5e5]'}`}
                                            />
                                        ))}
                                    </div>
                                    <span className="text-sm font-medium text-[#414141]">
                                        {completedGuidedChecks.length > 0
                                            ? `${completedGuidedChecks.length} check${completedGuidedChecks.length === 1 ? '' : 's'} completed`
                                            : 'No checks completed yet'}
                                    </span>
                                </div>
                            </div>

                            <div className="px-5 py-5">
                                <h3 className="text-[20px] font-black text-[#414141]">{activeCause.title}</h3>

                                <div className="mt-6">
                                    <div className="mb-2 flex items-center justify-between gap-4">
                                        <span className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Confidence</span>
                                        <span className="text-[18px] font-black text-[#414141]">{activeCause.confidenceScore}%</span>
                                    </div>
                                    <div className="h-2 rounded-full bg-[#ececec]">
                                        <div
                                            className="h-2 rounded-full bg-[#16cf57]"
                                            style={{ width: `${activeCause.confidenceScore}%` }}
                                        />
                                    </div>
                                </div>

                                <div className="mt-6 border-b border-[#d9d9d9] pb-6">
                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Why CarFido Thinks This</p>
                                    <p className="mt-3 text-[16px] font-medium leading-relaxed text-[#414141]">
                                        {activeCause.whyItFits}
                                    </p>
                                </div>

                                <div className="mt-5 border-b border-[#d9d9d9] pb-6">
                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">If This Diagnosis Fits</p>
                                    <div className="mt-4 grid gap-5 md:grid-cols-2">
                                        <MetricBlock label="Likely Repair" value={activeCause.likelyRepair} />
                                        {!hideCostEstimates && <MetricBlock label="DIY Repair Cost" value={activeCause.diyRepairCost} />}
                                        <MetricBlock label="Repair Time" value={activeCause.repairTime} />
                                        <MetricBlock label="DIY Difficulty">
                                            <div className="mt-1">
                                                <DifficultyBadge value={activeCause.diyDifficulty} />
                                            </div>
                                        </MetricBlock>
                                    </div>
                                </div>

                                <div className="pt-6">
                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Recommended Check</p>
                                    <p className="mt-3 text-[17px] font-black text-[#414141]">{activeCause.recommendedCheck}</p>
                                    {activeCause.checkDescription && (
                                        <p className="mt-3 text-[16px] font-medium text-[#414141]">
                                            {activeCause.checkDescription}
                                        </p>
                                    )}

                                    <div className="mt-4 flex flex-wrap items-center gap-6 text-[15px] font-medium text-[#414141]">
                                        {activeCause.toolSummary && (
                                            <div className="flex items-center gap-2">
                                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.1-3.1a6 6 0 0 1-7.9 7.9l-6 6a2 2 0 0 1-2.8-2.8l6-6a6 6 0 0 1 7.9-7.9l-3.1 3.1z" />
                                                </svg>
                                                <span>{activeCause.toolSummary}</span>
                                            </div>
                                        )}
                                        {activeCause.timeLabel && (
                                            <div className="flex items-center gap-2">
                                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="9" />
                                                    <path d="M12 7v5l3 3" />
                                                </svg>
                                                <span>{activeCause.timeLabel}</span>
                                            </div>
                                        )}
                                    </div>

                                    {activeCause.expectedResult && (
                                        <div className="mt-5 rounded-[22px] border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4">
                                            <div className="flex items-start gap-3">
                                                <div className="mt-0.5 text-[#414141]">
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <path d="M12 16v-4" />
                                                        <path d="M12 8h.01" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Expected Result</p>
                                                    <p className="mt-1 text-[15px] font-medium text-[#414141]">{activeCause.expectedResult}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-5 overflow-hidden rounded-[24px] border border-[#d9d9d9] bg-white">
                                        <button
                                            type="button"
                                            onClick={() => setOpenToolsByCause((prev) => ({ ...prev, [activeCause.id]: !toolsExpanded }))}
                                            className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#c8c8c8] text-[#afafaf]">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                        <circle cx="12" cy="12" r="9" />
                                                    </svg>
                                                </span>
                                                <div className="text-[15px] font-black text-[#414141]">
                                                    Tools &amp; parts
                                                    <span className="ml-2 text-sm font-semibold text-[#414141]">
                                                        {essentialTools.length + requiredParts.length} needed{requiredTools.length > essentialTools.length ? `, ${requiredTools.length - essentialTools.length} optional` : ''}
                                                    </span>
                                                </div>
                                            </div>
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className={`text-[#414141] transition-transform ${toolsExpanded ? 'rotate-180' : ''}`}
                                            >
                                                <path d="m6 9 6 6 6-6" />
                                            </svg>
                                        </button>

                                        {toolsExpanded && (
                                            <>
                                                <div className="border-t border-[#d9d9d9]">
                                                    {requiredTools.map((tool, index) => {
                                                        // isOwned: true = green badge, false = amber badge, undefined = no badge (not loaded yet)
                                                        const ownedNamesLower = userOwnedTools.map((t) => t.name.toLowerCase());
                                                        const isOwned = userOwnedTools.length > 0
                                                            ? ownedNamesLower.includes(tool.name.toLowerCase())
                                                            : undefined;
                                                        return (
                                                            <div key={tool.name} className={index === 0 ? '' : 'border-t border-[#d9d9d9]'}>
                                                                <ToolChecklistItem
                                                                    tool={tool}
                                                                    checked={Boolean(confirmedTools[tool.name])}
                                                                    onToggle={() => toggleTool(activeCause.id, tool.name)}
                                                                    isOwned={isOwned}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                    {requiredParts.map((part, index) => (
                                                        <div
                                                            key={`part-${part.name}`}
                                                            className={(requiredTools.length > 0 || index > 0) ? 'border-t border-[#d9d9d9]' : ''}
                                                        >
                                                            <div className="flex items-start gap-3 px-4 py-3">
                                                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#c8c8c8] text-[#414141]">
                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                                                        <path d="M3.27 6.96 12 12.01l8.73-5.05" />
                                                                        <path d="M12 22.08V12" />
                                                                    </svg>
                                                                </span>
                                                                <div className="min-w-0">
                                                                    <div className="text-[15px] font-black text-[#414141]">{part.name}</div>
                                                                    {part.notes && (
                                                                        <div className="mt-0.5 text-sm font-medium text-[#414141]">{part.notes}</div>
                                                                    )}
                                                                    <div className="mt-0.5 text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Part</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="border-t border-[#d9d9d9] px-4 py-4">
                                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Before You Start</p>
                                                    <p className="mt-2 text-[15px] font-medium text-[#414141]">
                                                        {ticketClosed
                                                            ? 'This ticket is already resolved. Guided checks are no longer available on this report.'
                                                            : "Grab what you have and begin. Each step tells you exactly what it needs. Ticking tools you own just saves them to your inventory (optional)."}
                                                    </p>
                                                    {ticketClosed ? (
                                                        <div className="mt-4 rounded-[18px] border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4 text-center">
                                                            <p className="text-[15px] font-black text-[#414141]">Ticket resolved</p>
                                                            <p className="mt-2 text-sm font-medium text-[#414141]">
                                                                This diagnostic is in review/resolved state, so `Continue Diagnostic Check` is unavailable.
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={handleContinueCheck}
                                                                className="mt-4 flex w-full items-center justify-center gap-3 rounded-xl bg-[#D81F26] px-5 py-3 text-base font-black text-white shadow-sm transition-all hover:bg-[#B8181E]"
                                                            >
                                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                                                    <circle cx="12" cy="12" r="9" />
                                                                    <path d="m10 8 6 4-6 4V8z" />
                                                                </svg>
                                                                Continue Diagnostic Check
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className="mt-6 border-t border-[#d9d9d9] pt-6">
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Need Help First?</p>
                                        <p className="mt-2 text-sm font-semibold text-[#414141]">
                                            Review real-world examples or watch a tutorial before starting.
                                        </p>
                                        <div className="mt-4 flex flex-col gap-3 md:flex-row">
                                            <button
                                                type="button"
                                                onClick={() => setShowForumModal(true)}
                                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-[#d9d9d9] bg-white px-5 py-3.5 text-base font-black text-[#414141] transition hover:bg-[#F9F9F9] active:scale-95"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                                </svg>
                                                Forum Examples
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShowVideoModal(true)}
                                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-[#d9d9d9] bg-white px-5 py-3.5 text-base font-black text-[#414141] transition hover:bg-[#F9F9F9] active:scale-95"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="9" />
                                                    <path d="m10 8 6 4-6 4V8z" />
                                                </svg>
                                                YouTube Tutorial
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </section>

                <ConfirmedDIYPlanSection
                    cause={activeCause}
                    completedCheck={confirmedActiveCheck}
                />

                <SectionTitle>Don't Want to DIY?</SectionTitle>
                <section className="overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm sm:rounded-[28px]">
                    <div className="px-4 py-4 sm:px-5 sm:py-5">
                        <div className="flex items-start gap-3">
                            <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f5f5f5] text-[#414141]">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 3h5v5" />
                                    <path d="M8 21H3v-5" />
                                    <path d="M21 3l-7 7" />
                                    <path d="M3 21l7-7" />
                                    <rect x="7" y="7" width="7" height="10" rx="1.5" />
                                    <path d="M9.5 10.5h2" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-[16px] font-black text-[#414141] sm:text-[18px]">Take it to a shop</h3>
                                <p className="mt-2 text-[14px] font-medium leading-relaxed text-[#414141] sm:text-[16px]">
                                    CarFido prepared a shop-ready symptom report you can share with a mechanic.
                                </p>
                            </div>
                        </div>

                        <div className="mt-6">
                            <MetricBlock label="Recommended Service" value={recommendedServiceLabel} />
                        </div>

                        {!hideCostEstimates && (
                            <p className="mt-3 text-sm font-medium text-[#414141]">
                                Final price may vary by shop, battery type, and diagnostic fee.
                            </p>
                        )}

                        <div className="mt-6">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Shop Report</p>
                            <p className="mt-2 text-[17px] font-black text-[#0a8f53]">Ready to Share</p>
                            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-[#414141]">
                                {['Symptoms', 'Most likely cause', 'Recommended checks', 'Vehicle info'].map((item) => (
                                    <span key={item} className="inline-flex items-center gap-2">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#19c95b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6 9 17l-5-5" />
                                        </svg>
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="mt-6 border-t border-[#ededed] pt-5">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Garage Progress</p>
                            <p className="mt-2 text-sm font-semibold leading-relaxed text-[#414141]">
                                Read-only shop progress for this report. Estimate decisions, scheduling, and messages stay in Garage chat.
                            </p>
                            <GarageProgressSummary
                                requests={garageProgressRequests}
                                isLoading={isGarageProgressLoading}
                                error={garageProgressError}
                                onOpenChat={handleOpenGarageChat}
                                onOpenGarage={handleBackToGarage}
                            />
                        </div>

                        <div className="mt-6 grid gap-2 sm:grid-cols-[1fr_auto]">
                            <button
                                type="button"
                                onClick={handleBackToGarage}
                                className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-[#414141] px-4 py-3 text-sm font-black text-white shadow-sm transition-all hover:bg-[#2A2A2A] sm:gap-3 sm:px-5 sm:text-base"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 3h5v5" />
                                    <path d="M8 21H3v-5" />
                                    <path d="M21 3l-7 7" />
                                    <path d="M3 21l7-7" />
                                    <rect x="7" y="7" width="7" height="10" rx="1.5" />
                                </svg>
                                Manage Garage Chats
                            </button>
                        </div>
                        <p className="mt-3 text-center text-sm font-medium text-[#414141]">
                            Estimate requests, approvals, status updates, and pickup messages are handled in Garage.
                        </p>
                    </div>

                </section>

                <SectionTitle>Shop-Ready Report</SectionTitle>
                <section className="overflow-hidden rounded-2xl border border-[#d9d9d9] bg-white shadow-sm sm:rounded-[28px]">
                    <div>
                        <button
                            type="button"
                            onClick={() => setIsShopPreviewOpen((prev) => !prev)}
                            className="flex w-full flex-col items-stretch gap-3 px-4 py-4 text-left transition-all hover:bg-[#F9F9F9] sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5"
                        >
                            <div className="flex min-w-0 items-start gap-3 sm:items-center">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f5f5f5] text-[#414141]">
                                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="6" y="3" width="12" height="18" rx="2" />
                                        <path d="M9 7h6" />
                                        <path d="M9 11h6" />
                                        <path d="M9 15h4" />
                                    </svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[16px] font-black leading-tight text-[#414141] sm:text-[18px]">Shop-Ready Report</p>
                                    <p className="mt-1 text-[14px] font-medium leading-relaxed text-[#414141] sm:text-[15px]">
                                        Symptoms, likely causes, evidence, and recommended checks
                                    </p>
                                </div>
                            </div>
                            <div className="ml-[52px] flex shrink-0 items-center gap-2 text-[#D81F26] sm:ml-0">
                                <span className="text-sm font-black sm:text-[15px]">{isShopPreviewOpen ? 'Hide' : 'Preview'}</span>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${isShopPreviewOpen ? 'rotate-180' : ''}`}>
                                    <path d="m6 9 6 6 6-6" />
                                </svg>
                            </div>
                        </button>

                        {isShopPreviewOpen && (
                            <div className="border-t border-[#d9d9d9] bg-white px-4 py-4 sm:px-5 sm:py-5">
                                <div className="grid gap-5 text-[14px] sm:text-[15px] md:grid-cols-2">
                                    <MetricBlock label="Report ID" value={reportIdentifier} />
                                    <MetricBlock label="Generated" value={reportGeneratedLabel} />
                                </div>

                                <div className="mt-6 grid gap-5 md:grid-cols-2">
                                    <div>
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Owner / Customer</p>
                                        <p className="mt-2 text-[16px] font-black text-[#414141] sm:text-[18px]">{customerName}</p>
                                        <p className="mt-1 break-words text-[13px] font-medium text-[#414141] sm:text-[15px]">
                                            {customerPhone} · {customerEmail}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Vehicle</p>
                                        <p className="mt-2 text-[14px] font-semibold leading-relaxed text-[#414141] sm:text-[16px]">{report.vehicle}</p>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Customer Complaint</p>
                                    <p className="mt-2 text-[14px] font-semibold leading-relaxed text-[#414141] sm:text-[16px]">{customerComplaint}</p>
                                </div>

                                {report.mechanicSummary && report.mechanicSummary !== customerComplaint && (
                                    <div className="mt-6">
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Shop-Ready Summary</p>
                                        <p className="mt-2 break-words text-[14px] font-medium leading-relaxed text-[#414141] sm:text-[15px]">{report.mechanicSummary}</p>
                                    </div>
                                )}

                                <div className="mt-6">
                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Observed Symptoms</p>
                                    {activeSymptoms.length > 0 ? (
                                        <ul className="mt-3 space-y-2 text-[14px] font-medium leading-relaxed text-[#414141] sm:text-[16px]">
                                            {activeSymptoms.map((symptom) => (
                                                <li key={symptom} className="flex items-start gap-3">
                                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#414141]" />
                                                    <span>{symptom}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="mt-2 text-[14px] font-medium text-[#414141] sm:text-[15px]">No observed symptoms captured.</p>
                                    )}
                                </div>

                                <div className="mt-6">
                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Likely Causes</p>
                                    <ol className="mt-3 space-y-2 text-[14px] font-medium leading-relaxed text-[#414141] sm:text-[16px]">
                                        {shopLikelyCauses.map((cause, index) => (
                                            <li key={cause.id} className="flex items-start gap-3">
                                                <span className="font-black text-[#414141]">{index + 1}.</span>
                                                <span>{cause.title}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                {completedGuidedChecks.length > 0 && (
                                    <div className="mt-6">
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Completed Diagnostic Checks</p>
                                        <div className="mt-3 space-y-3">
                                            {completedGuidedChecks.map((check) => (
                                                <div key={`${check.causeId}-${check.updatedAt}`} className="rounded-[20px] border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-[14px] font-black text-[#414141] sm:text-[16px]">{check.recommendedCheck}</p>
                                                        <span className={`rounded-full px-3 py-1 text-[12px] font-black ${
                                                            check.outcome === 'normal'
                                                                ? 'bg-[#eefbf3] text-[#0a8f53]'
                                                                : check.outcome === 'issue'
                                                                ? 'bg-[#fff1f1] text-[#d44848]'
                                                                : 'bg-[#fff7e8] text-[#b7791f]'
                                                        }`}>
                                                            {check.outcomeLabel}
                                                        </span>
                                                    </div>
                                                    <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#414141]">{check.summary}</p>
                                                    {check.notes && (
                                                        <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#414141]">
                                                            Notes: {check.notes}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {performedDiySteps.length > 0 && (
                                    <div className="mt-6">
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Performed DIY Steps</p>
                                        <div className="mt-3 space-y-3">
                                            {performedDiySteps.map((guide, gi) => (
                                                <div key={`${guide.causeId || 'diy'}-${guide.completedAt || gi}`} className="rounded-[20px] border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-4">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-[14px] font-black text-[#414141] sm:text-[16px]">{guide.repairTitle || guide.causeTitle || 'DIY repair'}</p>
                                                        <span className="rounded-full bg-[#eefbf3] px-3 py-1 text-[12px] font-black text-[#0a8f53]">Completed</span>
                                                    </div>
                                                    {guide.steps.length > 0 && (
                                                        <ol className="mt-3 list-decimal space-y-2 pl-5 text-[14px] font-medium leading-relaxed text-[#414141] sm:text-[15px]">
                                                            {guide.steps.map((step, si) => (
                                                                <li key={si}>
                                                                    <span className="font-black">{step.instruction || step.title || `Step ${si + 1}`}</span>
                                                                    {step.detail && <span className="block font-medium text-[#414141]">{step.detail}</span>}
                                                                </li>
                                                            ))}
                                                        </ol>
                                                    )}
                                                    {guide.note && (
                                                        <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#414141]">Notes: {guide.note}</p>
                                                    )}
                                                    {guide.mileage && (
                                                        <p className="mt-1 text-[14px] font-medium leading-relaxed text-[#414141]">Odometer: {guide.mileage}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {evidenceMedia.length > 0 && (
                                    <div className="mt-6">
                                        <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Evidence</p>
                                        <div className="mt-3 flex flex-wrap gap-3">
                                            {evidenceMedia.map((media, i) => {
                                                const label = media.caption || media.name || media.original_filename || `Attachment ${i + 1}`;
                                                if (media.isImage) {
                                                    return (
                                                        <a
                                                            key={media.attachment_id || i}
                                                            href={media.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={label}
                                                            className="group block h-24 w-24 overflow-hidden rounded-xl border border-[#d9d9d9] bg-[#F9F9F9]"
                                                        >
                                                            <img src={media.url} alt={label} loading="lazy" className="h-full w-full object-cover transition group-hover:opacity-90" />
                                                        </a>
                                                    );
                                                }
                                                return (
                                                    <a
                                                        key={media.attachment_id || i}
                                                        href={media.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={label}
                                                        className="flex h-24 w-24 flex-col items-center justify-center gap-1.5 rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-2 text-center text-[#414141] transition hover:border-[#414141]"
                                                    >
                                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            {media.isVideo
                                                                ? (<><rect x="2" y="5" width="14" height="14" rx="2" /><path d="m22 8-6 4 6 4V8Z" /></>)
                                                                : (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>)}
                                                        </svg>
                                                        <span className="w-full truncate text-[10px] font-bold">{media.isVideo ? 'Video' : 'File'}</span>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-6">
                                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Recommended Checks</p>
                                    <ul className="mt-3 space-y-2 text-[14px] font-medium leading-relaxed text-[#414141] sm:text-[16px]">
                                        {recommendedChecks.map((check) => (
                                            <li key={check} className="flex items-start gap-3">
                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#414141]" />
                                                <span>{check}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="mt-6 grid gap-2 sm:flex sm:flex-wrap sm:gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCopySummary}
                                        className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141] transition-all hover:bg-[#F9F9F9] sm:w-auto sm:gap-3 sm:rounded-2xl sm:px-5 sm:text-[15px]"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="9" y="9" width="13" height="13" rx="2" />
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                        </svg>
                                        Copy Summary
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDownloadReport}
                                        className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141] transition-all hover:bg-[#F9F9F9] sm:w-auto sm:gap-3 sm:rounded-2xl sm:px-5 sm:text-[15px]"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <path d="M7 10l5 5 5-5" />
                                            <path d="M12 15V3" />
                                        </svg>
                                        Download Report
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleShareReport}
                                        className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-[#d9d9d9] bg-white px-4 py-3 text-sm font-black text-[#414141] transition-all hover:bg-[#F9F9F9] sm:w-auto sm:gap-3 sm:rounded-2xl sm:px-5 sm:text-[15px]"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="18" cy="5" r="3" />
                                            <circle cx="6" cy="12" r="3" />
                                            <circle cx="18" cy="19" r="3" />
                                            <path d="m8.6 13.5 6.8 4" />
                                            <path d="m15.4 6.5-6.8 4" />
                                        </svg>
                                        Share Report
                                    </button>
                                </div>
                                {copyNotice && (
                                    <p className="mt-3 text-sm font-bold text-[#0a8f53]">{copyNotice}</p>
                                )}
                            </div>
                        )}
                    </div>

                        <div className="mt-6 border-t border-[#e5eef6] pt-5">
                            <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#5f728a]">Shops Contacted</p>
                            <p className="mt-2 text-sm font-semibold leading-relaxed text-[#5f728a]">
                                Track estimates and appointments for this report.
                            </p>
                            <GarageProgressSummary
                                requests={garageProgressRequests}
                                isLoading={isGarageProgressLoading}
                                error={garageProgressError}
                                onOpenChat={handleOpenGarageChat}
                                onOpenGarage={handleBackToGarage}
                            />
                        </div>

                        <div className="mt-6 grid gap-2 sm:grid-cols-[1fr_auto]">
                            <button
                                type="button"
                                onClick={handleBackToGarage}
                                className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-[#0072a8] px-4 py-3 text-sm font-black text-white shadow-sm transition-all hover:bg-[#006492] sm:gap-3 sm:px-5 sm:text-base"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 3h5v5" />
                                    <path d="M8 21H3v-5" />
                                    <path d="M21 3l-7 7" />
                                    <path d="M3 21l7-7" />
                                    <rect x="7" y="7" width="7" height="10" rx="1.5" />
                                </svg>
                                View all shop requests
                            </button>
                        </div>
                        <p className="mt-3 text-center text-sm font-medium text-[#6b7f96]">
                            Estimate requests, approvals, status updates, and pickup messages are handled in your shop conversations.
                        </p>
                </section>

                <footer className="mt-6 flex items-center justify-center gap-2 px-2 text-center text-[11px] font-semibold leading-relaxed text-[#414141] sm:mt-7 sm:text-xs">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 2v4" />
                        <path d="M16 2v4" />
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M3 10h18" />
                    </svg>
                    <span className="min-w-0">Generated {reportGeneratedLabel} - CarFido Diagnostics</span>
                </footer>
            </main>

            {/* Forum Examples popup — top 5 forum results (each opens its search) */}
            {showForumModal && activeCause && (() => {
                const forumVehicle = report?.vehicle || report?.vehicleLabel || report?.vehicle_label || 'your vehicle';
                const forumCause = activeCause.title || activeCause.name || activeCause.recommendedCheck || 'this diagnosis';
                const vehTokens = String(forumVehicle).trim().split(/\s+/).filter(Boolean);
                const forumMake = /^(19|20)\d{2}$/.test(vehTokens[0] || '') ? (vehTokens[1] || '') : (vehTokens[0] || '');
                const fq = (extra) => encodeURIComponent(`${forumVehicle} ${forumCause} ${extra}`.trim());
                const forumResults = (Array.isArray(activeCause.forumResults) && activeCause.forumResults.length
                    ? activeCause.forumResults
                    : [
                        { title: `${forumVehicle}: ${forumCause} discussion`, source: 'Reddit r/MechanicAdvice', description: 'Community diagnosis threads related to this cause.', url: `https://www.reddit.com/r/MechanicAdvice/search/?q=${fq('')}&sort=relevance` },
                        { title: `${forumVehicle} ${forumCause}: owner threads`, source: 'Reddit r/AskMechanics', description: 'Real owners describing the same symptom and fixes.', url: `https://www.reddit.com/r/AskMechanics/search/?q=${fq('')}&sort=relevance` },
                        { title: `${forumVehicle} ${forumCause} forum`, source: 'Google Forums Search', description: 'Vehicle-specific forum threads on this diagnosis.', url: `https://www.google.com/search?q=${fq('forum')}` },
                        { title: `${forumMake || 'Owner'} owners forum: ${forumCause}`, source: 'Owner Community', description: 'Model-specific owner community discussions.', url: `https://www.google.com/search?q=${fq('owners forum')}` },
                        { title: `${forumVehicle} ${forumCause}: reported problems`, source: 'Complaints & Q&A', description: 'Documented complaints and mechanic answers.', url: `https://www.google.com/search?q=${fq('problem OR complaint OR fix')}` },
                    ]
                ).slice(0, 5);
                return (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowForumModal(false)}>
                        <div className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(48,48,48,0.28)]" onClick={(event) => event.stopPropagation()}>
                            <div className="flex items-center justify-between gap-3 border-b border-[#f2f2f2] px-5 py-4">
                                <div className="flex items-center gap-2">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#414141" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                                    <span className="text-[17px] font-black text-[#414141]">Real-world Examples</span>
                                </div>
                                <button type="button" onClick={() => setShowForumModal(false)} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full text-[#414141] transition hover:bg-[#f4f4f4]">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                                </button>
                            </div>
                            <p className="px-5 pt-3.5 text-sm font-semibold text-[#414141]">{forumCause}</p>
                            <div className="max-h-[60vh] overflow-y-auto px-5 pb-1">
                                {forumResults.map((result, index) => (
                                    <a key={`${result.title}-${index}`} href={result.url} target="_blank" rel="noopener noreferrer" className={`group flex items-start justify-between gap-3 py-3.5 ${index === forumResults.length - 1 ? '' : 'border-b border-[#f4f4f4]'}`}>
                                        <div className="min-w-0">
                                            <p className="text-base font-black leading-snug text-[#414141] group-hover:text-[#D81F26]">{result.title}</p>
                                            <p className="mt-1 text-[11px] font-black uppercase tracking-[0.06em] text-[#8C8C8C]">{result.source}</p>
                                            <p className="mt-1 text-sm font-medium text-[#414141]">{result.description}</p>
                                        </div>
                                        <svg className="mt-1 shrink-0 text-[#414141] group-hover:text-[#D81F26]" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
                                    </a>
                                ))}
                            </div>
                            <div className="border-t border-[#f2f2f2] px-5 py-3.5">
                                <p className="text-[13px] font-medium leading-relaxed text-[#939393]">
                                    Forum posts are community examples, not verified repair instructions. Use CarFido's guided checks to confirm your vehicle's issue.
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* YouTube Tutorial popup — top 5 video tutorials (each opens a YouTube search) */}
            {showVideoModal && activeCause && (() => {
                const vidVehicle = report?.vehicle || report?.vehicleLabel || report?.vehicle_label || 'your vehicle';
                const vidCause = activeCause.title || activeCause.name || activeCause.recommendedCheck || 'this diagnosis';
                const vidTokens = String(vidVehicle).trim().split(/\s+/).filter(Boolean);
                const vidMake = /^(19|20)\d{2}$/.test(vidTokens[0] || '') ? (vidTokens[1] || '') : (vidTokens[0] || '');
                const yt = (extra) => `https://www.youtube.com/results?search_query=${encodeURIComponent(`${vidVehicle} ${vidCause} ${extra}`.trim())}`;
                const videoResults = (Array.isArray(activeCause.videoResults) && activeCause.videoResults.length
                    ? activeCause.videoResults
                    : [
                        { title: `${vidVehicle} ${vidCause}: diagnosis`, source: 'YouTube', description: 'Step-by-step diagnosis walkthroughs.', url: yt('diagnosis') },
                        { title: `${vidVehicle} ${vidCause}: how to fix`, source: 'YouTube', description: 'Repair and replacement tutorials.', url: yt('how to fix replace') },
                        { title: `${vidVehicle} ${vidCause}: symptoms`, source: 'YouTube', description: 'What the problem looks and sounds like.', url: yt('symptoms sound') },
                        { title: `${activeCause.recommendedCheck || vidCause}: how to test`, source: 'YouTube', description: 'How to perform the recommended check.', url: `https://www.youtube.com/results?search_query=${encodeURIComponent(`${vidVehicle} ${activeCause.recommendedCheck || vidCause} test how to`.trim())}` },
                        { title: `${vidMake || 'Owner'} ${vidCause}: repair guide`, source: 'YouTube', description: 'Model-specific repair guides.', url: yt('repair guide') },
                    ]
                ).slice(0, 5);
                return (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowVideoModal(false)}>
                        <div className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(48,48,48,0.28)]" onClick={(event) => event.stopPropagation()}>
                            <div className="flex items-center justify-between gap-3 border-b border-[#f2f2f2] px-5 py-4">
                                <div className="flex items-center gap-2">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="#ef4444" stroke="none"><path d="M23 12s0-3.5-.45-5.18a2.6 2.6 0 0 0-1.83-1.84C19.05 4.5 12 4.5 12 4.5s-7.05 0-8.72.48A2.6 2.6 0 0 0 1.45 6.82C1 8.5 1 12 1 12s0 3.5.45 5.18a2.6 2.6 0 0 0 1.83 1.84C4.95 19.5 12 19.5 12 19.5s7.05 0 8.72-.48a2.6 2.6 0 0 0 1.83-1.84C23 15.5 23 12 23 12z" /><path d="M9.75 15.5v-7l6 3.5z" fill="#fff" /></svg>
                                    <span className="text-[17px] font-black text-[#414141]">Video Tutorials</span>
                                </div>
                                <button type="button" onClick={() => setShowVideoModal(false)} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full text-[#414141] transition hover:bg-[#f4f4f4]">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                                </button>
                            </div>
                            <p className="px-5 pt-3.5 text-sm font-semibold text-[#414141]">{vidCause}</p>
                            <div className="max-h-[60vh] overflow-y-auto px-5 pb-1">
                                {videoResults.map((result, index) => (
                                    <a key={`${result.title}-${index}`} href={result.url} target="_blank" rel="noopener noreferrer" className={`group flex items-start justify-between gap-3 py-3.5 ${index === videoResults.length - 1 ? '' : 'border-b border-[#f4f4f4]'}`}>
                                        <div className="flex min-w-0 items-start gap-3">
                                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#fee2e2]">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#ef4444" stroke="none"><path d="M23 12s0-3.5-.45-5.18a2.6 2.6 0 0 0-1.83-1.84C19.05 4.5 12 4.5 12 4.5s-7.05 0-8.72.48A2.6 2.6 0 0 0 1.45 6.82C1 8.5 1 12 1 12s0 3.5.45 5.18a2.6 2.6 0 0 0 1.83 1.84C4.95 19.5 12 19.5 12 19.5s7.05 0 8.72-.48a2.6 2.6 0 0 0 1.83-1.84C23 15.5 23 12 23 12z" /><path d="M9.75 15.5v-7l6 3.5z" fill="#fff" /></svg>
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-base font-black leading-snug text-[#414141] group-hover:text-[#D81F26]">{result.title}</p>
                                                <p className="mt-1 text-[11px] font-black uppercase tracking-[0.06em] text-[#c0392b]">{result.source}</p>
                                                <p className="mt-1 text-sm font-medium text-[#414141]">{result.description}</p>
                                            </div>
                                        </div>
                                        <svg className="mt-1 shrink-0 text-[#414141] group-hover:text-[#D81F26]" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
                                    </a>
                                ))}
                            </div>
                            <div className="border-t border-[#f2f2f2] px-5 py-3.5">
                                <p className="text-[13px] font-medium leading-relaxed text-[#939393]">
                                    Videos are community tutorials, not verified repair instructions. Use CarFido's guided checks to confirm your vehicle's issue.
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default DiagnosticReportPage;
